/**
 * demoOverlay — single source of truth for the data the app shows
 * when DemoModeContext.isDemo is true.
 *
 * Why this exists:
 *   - The earlier mock data lived in orbitData.ts / mockData.ts with
 *     hardcoded 2024 dates. By 2026 those dates were stale and every
 *     "today / this week" stat computed to 0, so demo mode looked
 *     dead despite being on. This file recomputes dates relative to
 *     `now` at module load — every page-view gets a fresh dataset
 *     where today is genuinely today.
 *
 *   - Components consume project rows two ways: typed Project objects
 *     (camelCase) and raw Supabase rows (snake_case). Demo objects
 *     here include BOTH so whichever surface reads them gets the
 *     fields it expects without per-component shimming.
 *
 *   - `getDemoStats()` returns the precomputed metrics the dashboard
 *     ribbons need (contracts sent, payments received, etc.) which
 *     don't currently come from any single table query.
 *
 *   - MEDIA-2: every photograph on this cast is now one of Rennan's
 *     own frames, not stock. They come in from `@/data/demoPhotos`,
 *     which is generated from the MEDIA-1 manifest alongside its iOS
 *     twin — so `demo-g-1` photo 3 is literally the same file on the
 *     web and on the phone. Do not paste storage URLs in here; add
 *     them to the manifest and re-run the emitter.
 *
 *   - DEMO-4: this file is now the ONLY demo dataset. The 2024-era
 *     fallbacks in `orbitData.ts` / `mockData.ts` (Martinez Wedding,
 *     "Summer Campaign 2024", January-2024 creator join dates) are no
 *     longer reachable from any `isDemo` branch — every service now
 *     resolves against the cast below, so a demo can't show 2024 work
 *     next to today's. Four entities were added for the core-loop
 *     walk: `demoGalleryPicks`, `demoGalleryComments`,
 *     `demoProjectFiles`, `demoDeliverableComments`, plus
 *     `demoCreators` for the admin roster.
 */

import { demoGalleryCovers, demoPhotosFor } from '@/data/demoPhotos';

const now = new Date();
const dayMs = 86400000;

const isoDate = (offsetDays: number): string =>
  new Date(now.getTime() + offsetDays * dayMs).toISOString().slice(0, 10);

const isoTimestamp = (offsetDays: number, hour = 10): string => {
  const d = new Date(now.getTime() + offsetDays * dayMs);
  d.setHours(hour, 0, 0, 0);
  return d.toISOString();
};

// ──────────────────────────────────────────────────────────────────
// Clients
// ──────────────────────────────────────────────────────────────────
export const demoClients = [
  { id: 'demo-c-1',  name: 'Sarah & Ben Martinez', email: 'sarah.mitchell@gmail.com', phone: '+1 (415) 555-0182', brand: null, created_at: isoTimestamp(-22), notes: 'June wedding · Aspen Lodge' },
  { id: 'demo-c-2',  name: 'Hollins Family',       email: 'jen.hollins@me.com',        phone: '+1 (212) 555-0410', brand: null, created_at: isoTimestamp(-5),  notes: 'Family session · newborn + toddler' },
  { id: 'demo-c-3',  name: 'Nova Coffee Co',       email: 'creative@novacoffee.co',    phone: '+1 (415) 555-0944', brand: 'Nova Coffee Co',    created_at: isoTimestamp(-12), notes: 'Brand campaign · Q2 launch' },
  { id: 'demo-c-4',  name: 'Marcus & Elle',        email: 'marcus.elle@gmail.com',     phone: '+1 (310) 555-0149', brand: null, created_at: isoTimestamp(-18), notes: 'Engagement · downtown rooftop' },
  { id: 'demo-c-5',  name: 'The Chen Family',      email: 'ines.chen.studio@gmail.com', phone: '+1 (646) 555-0231', brand: null, created_at: isoTimestamp(-7),  notes: 'Editorial · Vogue submission' },
  { id: 'demo-c-6',  name: 'Willow Boutique',      email: 'hello@willowboutique.co',   phone: null,                brand: 'Willow Boutique',   created_at: isoTimestamp(-15), notes: 'Brand refresh · moodboards' },
  { id: 'demo-c-7',  name: 'Dr. Alicia Reyes',     email: 'dr.reyes@practicehealth.com', phone: '+1 (917) 555-0288', brand: null, created_at: isoTimestamp(-9),  notes: 'Headshot session · press kit' },
  { id: 'demo-c-8',  name: 'Kayla + James',        email: 'kayla.james@email.com',     phone: '+1 (917) 555-0322', brand: null, created_at: isoTimestamp(-45), notes: 'Wedding · Hudson Valley' },
  { id: 'demo-c-9',  name: 'Whitmore Estate',      email: 'estate@whitmoregardens.com', phone: '+1 (203) 555-0714', brand: null, created_at: isoTimestamp(-30), notes: 'Estate portrait · outdoor' },
  { id: 'demo-c-10', name: 'Ember Restaurant',     email: 'gm@emberrestaurant.com',    phone: '+1 (415) 555-0817', brand: null, created_at: isoTimestamp(-3),  notes: 'Menu + interiors shoot' },
  { id: 'demo-c-11', name: 'The Andersson Wedding', email: 'mira.andersson@email.com', phone: '+1 (646) 555-0912', brand: null, created_at: isoTimestamp(-30), notes: 'Wedding · Brooklyn loft' },
  { id: 'demo-c-12', name: 'Sunset Kids',          email: 'hannah.sunset@gmail.com',   phone: '+1 (404) 555-0871', brand: null, created_at: isoTimestamp(-2),  notes: 'Family session · golden hour' },
];

// ──────────────────────────────────────────────────────────────────
// Projects — both camelCase (typed Project) and snake_case (DB row)
// fields included on every entry so any consumer is happy.
// ──────────────────────────────────────────────────────────────────
type DemoProject = Record<string, any>;

function makeProject(opts: {
  id: string;
  clientId: string;
  clientName: string;
  name: string;
  status: string;
  budget: number;
  scheduledOffset: number | null; // days from today; null if no shoot scheduled
  dueOffset: number; // days from today
  type: string;
  location?: string;
  description: string;
  createdOffset: number; // days ago
  doneMilestones: number;
  totalMilestones?: number;
}): DemoProject {
  const tot = opts.totalMilestones ?? 6;
  const milestones = Array.from({ length: tot }, (_, i) => ({
    id: `${opts.id}-ms-${i + 1}`,
    projectId: opts.id,
    project_id: opts.id,
    name: ['Discovery', 'Pre-production', 'Production', 'Editing', 'Review', 'Delivery'][i] || `Stage ${i + 1}`,
    completed: i < opts.doneMilestones,
    sortOrder: i + 1,
    sort_order: i + 1,
  }));
  return {
    id: opts.id,
    clientId: opts.clientId,
    client_id: opts.clientId,
    client: { id: opts.clientId, name: opts.clientName },
    name: opts.name,
    title: opts.name,
    status: opts.status,
    stage: opts.status,
    budget: opts.budget,
    value: opts.budget,
    type: opts.type,
    project_type: opts.type,
    location: opts.location ?? null,
    description: opts.description,
    dueDate: opts.dueOffset !== null ? isoDate(opts.dueOffset) : null,
    due_date: opts.dueOffset !== null ? isoDate(opts.dueOffset) : null,
    delivery_date: opts.dueOffset !== null ? isoDate(opts.dueOffset) : null,
    scheduled_date: opts.scheduledOffset !== null ? isoDate(opts.scheduledOffset) : null,
    shoot_date: opts.scheduledOffset !== null ? isoDate(opts.scheduledOffset) : null,
    createdAt: isoTimestamp(opts.createdOffset),
    created_at: isoTimestamp(opts.createdOffset),
    updated_at: isoTimestamp(Math.max(opts.createdOffset, -1)),
    milestones,
  };
}

// DEMO-1: cast aligned 1:1 with iOS `DemoSeed.projects` — same names,
// same client mapping — so keynote cuts between devices read as one
// studio. Stage ids use the canonical underscore form (`pre_production`)
// that ProductionBoard / ProjectStageRail bucket on.
export const demoProjects: DemoProject[] = [
  // Two TODAY shoots — drives "Today's shoots = 2"
  makeProject({ id: 'demo-p-1', clientId: 'demo-c-1', clientName: 'Sarah & Ben Martinez', name: 'Sarah & Ben — Wedding', status: 'production', budget: 4800, scheduledOffset: 0, dueOffset: 14, type: 'wedding', location: 'Aspen Lodge, CO', description: 'June wedding · Aspen Lodge — full day coverage + reception highlight film.', createdOffset: -22, doneMilestones: 2 }),
  makeProject({ id: 'demo-p-2', clientId: 'demo-c-4', clientName: 'Marcus & Elle', name: 'Marcus & Elle — Engagement', status: 'production', budget: 1600, scheduledOffset: 0, dueOffset: 7, type: 'engagement', location: 'Downtown rooftop', description: 'Golden-hour engagement session, downtown rooftop.', createdOffset: -18, doneMilestones: 2 }),

  // Three DUE THIS WEEK
  makeProject({ id: 'demo-p-3', clientId: 'demo-c-8', clientName: 'Kayla + James', name: 'Kayla + James — Wedding Edit', status: 'editing', budget: 6200, scheduledOffset: -10, dueOffset: 3, type: 'wedding', location: 'Hudson Valley, NY', description: 'Highlight film + 200 edited photos.', createdOffset: -45, doneMilestones: 4 }),
  makeProject({ id: 'demo-p-4', clientId: 'demo-c-3', clientName: 'Nova Coffee Co', name: 'Nova Coffee — Brand Shoot', status: 'review', budget: 2200, scheduledOffset: -8, dueOffset: 5, type: 'brand', location: 'Brooklyn studio', description: 'Brand campaign visuals — Q2 launch.', createdOffset: -12, doneMilestones: 5 }),
  makeProject({ id: 'demo-p-5', clientId: 'demo-c-2', clientName: 'Hollins Family', name: 'Hollins Family Session', status: 'editing', budget: 850, scheduledOffset: -3, dueOffset: 6, type: 'family', location: 'Hollins residence', description: 'Newborn + toddler session, in-home.', createdOffset: -5, doneMilestones: 4 }),

  // Active projects in various stages — drives "Active projects" count high
  makeProject({ id: 'demo-p-6', clientId: 'demo-c-11', clientName: 'The Andersson Wedding', name: 'Andersson — Wedding', status: 'pre_production', budget: 7400, scheduledOffset: 28, dueOffset: 60, type: 'wedding', location: 'Wythe Hotel, Brooklyn', description: 'Brooklyn loft wedding — film + photo team.', createdOffset: -30, doneMilestones: 1 }),
  makeProject({ id: 'demo-p-7', clientId: 'demo-c-10', clientName: 'Ember Restaurant', name: 'Ember — Menu + Interiors', status: 'pre_production', budget: 9200, scheduledOffset: 21, dueOffset: 45, type: 'commercial', location: 'Ember Restaurant', description: 'Menu + interior stills for Q3 launch.', createdOffset: -9, doneMilestones: 1 }),
  makeProject({ id: 'demo-p-8', clientId: 'demo-c-5', clientName: 'The Chen Family', name: 'Chen — Editorial', status: 'production', budget: 3800, scheduledOffset: 4, dueOffset: 18, type: 'editorial', location: 'Long Island City', description: 'Vogue submission — testing & main shoot days.', createdOffset: -7, doneMilestones: 2 }),
  makeProject({ id: 'demo-p-9', clientId: 'demo-c-6', clientName: 'Willow Boutique', name: 'Willow — Brand Refresh', status: 'discovery', budget: 4400, scheduledOffset: 12, dueOffset: 35, type: 'brand', location: 'Willow Boutique HQ', description: 'Brand visual refresh — moodboards + shoot plan.', createdOffset: -15, doneMilestones: 0 }),
  makeProject({ id: 'demo-p-10', clientId: 'demo-c-12', clientName: 'Sunset Kids', name: 'Sunset Kids — Family', status: 'discovery', budget: 1400, scheduledOffset: 9, dueOffset: 24, type: 'family', location: 'Piedmont Park, ATL', description: 'Golden-hour family session.', createdOffset: -3, doneMilestones: 0 }),

  // Recently delivered — gives "this month delivered" history
  makeProject({ id: 'demo-p-11', clientId: 'demo-c-3', clientName: 'Nova Coffee Co', name: 'Nova Coffee — Q1 Spot', status: 'delivered', budget: 3200, scheduledOffset: -28, dueOffset: -8, type: 'brand', location: 'Nova Coffee flagship', description: 'Q1 brand spot delivered, archived.', createdOffset: -55, doneMilestones: 6 }),
  makeProject({ id: 'demo-p-12', clientId: 'demo-c-7', clientName: 'Dr. Alicia Reyes', name: 'Dr. Reyes — Headshot', status: 'delivered', budget: 2400, scheduledOffset: -32, dueOffset: -12, type: 'portrait', location: 'Studio', description: 'Press-kit headshots delivered.', createdOffset: -50, doneMilestones: 6 }),
];

// ──────────────────────────────────────────────────────────────────
// Inquiries — 12 entries with realistic recency mix.
// 2 today, 4 idle (3-7 days), 4 follow-up, 2 accepted.
// ──────────────────────────────────────────────────────────────────
type DemoInquiry = Record<string, any>;

function makeInquiry(opts: {
  id: string;
  name: string;
  email: string;
  phone?: string;
  status: 'pending' | 'accepted' | 'follow_up' | 'declined';
  serviceType: string;
  interest: string;
  budget: string;
  daysAgo: number;
  package?: string;
  location?: string;
  notes?: string;
  /**
   * UX-5 — link to the demo project this lead converted into. An
   * accepted lead with NO project link now renders a persistent
   * "Project not created — retry" chip (that state means the
   * accept→create-project write failed), so the two accepted demo
   * leads MUST carry their real project ids or the demo shows a
   * failure that never happened.
   */
  projectId?: string;
}): DemoInquiry {
  const submittedAt = isoTimestamp(-opts.daysAgo, 11);
  return {
    id: opts.id,
    name: opts.name,
    email: opts.email,
    phone: opts.phone,
    interest: opts.interest,
    budget: opts.budget,
    status: opts.status,
    projectStage: 'lead',
    location: opts.location,
    packageSelected: opts.package,
    package: opts.package,
    projectDescription: opts.notes,
    description: opts.notes,
    serviceType: opts.serviceType,
    service_type: opts.serviceType,
    submittedAt,
    submitted_at: submittedAt,
    created_at: submittedAt,
    clientPortalEnabled: opts.status === 'accepted',
    portal_enabled: opts.status === 'accepted',
    ...(opts.projectId
      ? { projectId: opts.projectId, project_id: opts.projectId }
      : {}),
  };
}

export const demoInquiries: DemoInquiry[] = [
  // 2 inquiries today — drives "Inquiries today = 2"
  makeInquiry({ id: 'demo-i-1', name: 'Riley Cohen', email: 'riley.cohen@gmail.com', phone: '+1 (917) 555-0288', status: 'pending', serviceType: 'wedding', interest: 'Wedding photography', budget: '$5,000 - $8,000', daysAgo: 0, package: 'Full day · 2 photographers', location: 'Hudson, NY', notes: "Outdoor ceremony + barn reception. Looking at September 2026." }),
  makeInquiry({ id: 'demo-i-2', name: 'Maya Patel', email: 'maya.patel.studio@gmail.com', phone: '+1 (415) 555-0119', status: 'pending', serviceType: 'brand', interest: 'Brand video', budget: '$3,000 - $5,000', daysAgo: 0, package: 'Half-day brand shoot', location: 'San Francisco, CA', notes: 'Skincare line launching, need lifestyle + product hero.' }),

  // 4 idle (3-7 days, status pending) — drives "Idle leads = 4"
  makeInquiry({ id: 'demo-i-3', name: 'Olivia Bennett', email: 'olivia.b@gmail.com', phone: '+1 (212) 555-0744', status: 'pending', serviceType: 'wedding', interest: 'Wedding photo + video', budget: '$8,000 - $12,000', daysAgo: 4, package: 'Full day + film', location: 'Brooklyn, NY' }),
  makeInquiry({ id: 'demo-i-4', name: 'Daniel Wong', email: 'd.wong@email.com', phone: '+1 (646) 555-0188', status: 'pending', serviceType: 'engagement', interest: 'Engagement session', budget: '$1,500 - $2,500', daysAgo: 5, location: 'Manhattan, NY' }),
  makeInquiry({ id: 'demo-i-5', name: 'The Lockharts', email: 'lockhart.family@gmail.com', status: 'pending', serviceType: 'family', interest: 'Family session', budget: '$800 - $1,200', daysAgo: 6, location: 'Westchester, NY' }),
  makeInquiry({ id: 'demo-i-6', name: 'Saanvi Iyer', email: 'saanvi@iyerstudio.com', phone: '+1 (510) 555-0322', status: 'pending', serviceType: 'editorial', interest: 'Editorial', budget: '$4,000 - $6,000', daysAgo: 7, location: 'Oakland, CA', notes: 'Editorial submission for fashion mag.' }),

  // 4 follow-up
  makeInquiry({ id: 'demo-i-7', name: 'Theo Marshall', email: 'theo.m@gmail.com', phone: '+1 (404) 555-0922', status: 'follow_up', serviceType: 'wedding', interest: 'Wedding film', budget: '$6,000 - $9,000', daysAgo: 12, package: 'Cinematic film package', location: 'Atlanta, GA' }),
  makeInquiry({ id: 'demo-i-8', name: 'Jordan & Eve', email: 'jordan.eve@email.com', status: 'follow_up', serviceType: 'engagement', interest: 'Engagement', budget: '$1,200 - $2,000', daysAgo: 14 }),
  makeInquiry({ id: 'demo-i-9', name: 'Halcyon Co.', email: 'studio@halcyon.co', phone: '+1 (323) 555-0288', status: 'follow_up', serviceType: 'brand', interest: 'Brand campaign', budget: '$10,000 - $15,000', daysAgo: 15, location: 'Los Angeles, CA', notes: 'Q3 brand campaign — looking at multi-day production.' }),
  makeInquiry({ id: 'demo-i-10', name: 'Ravi & Anjali', email: 'ravi.anjali@gmail.com', phone: '+1 (310) 555-0671', status: 'follow_up', serviceType: 'wedding', interest: 'Wedding photo', budget: '$7,000 - $10,000', daysAgo: 18, location: 'Pasadena, CA' }),

  // 2 accepted — converted to clients (same names as demoClients/iOS)
  makeInquiry({ id: 'demo-i-11', name: 'Sarah & Ben Martinez', email: 'sarah.mitchell@gmail.com', phone: '+1 (415) 555-0182', status: 'accepted', serviceType: 'wedding', interest: 'Wedding photo + video', budget: '$4,800', daysAgo: 22, package: 'Full day · Wedding', location: 'Aspen Lodge, CO', projectId: 'demo-p-1' }),
  makeInquiry({ id: 'demo-i-12', name: 'Nova Coffee Co', email: 'creative@novacoffee.co', status: 'accepted', serviceType: 'brand', interest: 'Brand campaign', budget: '$2,200', daysAgo: 12, package: 'Half-day brand shoot', location: 'San Francisco, CA', projectId: 'demo-p-4' }),
];

// ──────────────────────────────────────────────────────────────────
// Goals
// ──────────────────────────────────────────────────────────────────
// PASSPORT-2 — goal_progress rows: one sentence, rings you close.
// Counters carry a live value (the server counts it for real
// studios; here the numbers are the studio's story). Two stamped
// worlds so the Passport pages read as a studio with history.
const demoRing = (goalId: string, i: number, r: { kind: 'step' | 'counter'; label: string; ink: 'bookings' | 'craft' | 'revenue' | 'audience' | 'ops'; metric?: 'bookings' | 'revenue' | 'clients' | 'galleries'; target?: number; value?: number; closedDaysAgo?: number | null }) => ({
  id: `${goalId}-r${i}`, position: i, kind: r.kind, label: r.label, ink: r.ink,
  metric: r.metric ?? null, target: r.target ?? null,
  closed_at: r.closedDaysAgo != null ? isoTimestamp(-r.closedDaysAgo, 15) : null,
  value: r.kind === 'counter' ? (r.closedDaysAgo != null ? r.target ?? 0 : r.value ?? 0) : null,
});
function makeGoal(opts: { id: string; title: string; why?: string; world: string; dueInDays?: number; pinned?: boolean; createdDaysAgo: number; completedDaysAgo?: number; stampedDaysAgo?: number; rings: Parameters<typeof demoRing>[2][] }) {
  const rings = opts.rings.map((r, i) => demoRing(opts.id, i, r));
  return {
    id: opts.id, org_id: 'demo-org', created_by: 'demo-studio-user', title: opts.title, why: opts.why ?? null, world_id: opts.world,
    due_on: opts.dueInDays != null ? isoDate(opts.dueInDays) : null, pinned: !!opts.pinned,
    completed_at: opts.completedDaysAgo != null ? isoTimestamp(-opts.completedDaysAgo, 16) : null,
    stamped_at: opts.stampedDaysAgo != null ? isoTimestamp(-opts.stampedDaysAgo, 17) : null,
    archived_at: null, created_at: isoTimestamp(-opts.createdDaysAgo), updated_at: isoTimestamp(-Math.min(opts.createdDaysAgo, 3)),
    rings, rings_total: rings.length, rings_closed: rings.filter((r) => r.closed_at).length,
  };
}
// DEMO-WORLDS-1 (Sep 28) — every `world` here is a world id from
// galaxy3d/worlds.ts, one live goal per world. demo-g-3 and demo-g-4
// used to carry glyph keys ('lighthouse', 'djclub'), which are not
// worlds: `worldOf` fell back to The Dunes for both (a duplicate of
// demo-g-1's world on Today) and the 3D galaxy, which charts by world
// id, never showed them. Tidewater and Halcyon are the Ivory system's
// next free worlds; Halcyon is also demo-g-4's "Pitch Halcyon Co." ring.
export const demoGoals = [
  makeGoal({
    id: 'demo-g-1', title: 'Ten paying clients', why: 'Where it all started.', world: 'dunes', createdDaysAgo: 170, completedDaysAgo: 75, stampedDaysAgo: 75,
    rings: [{ kind: 'counter', label: 'New clients', ink: 'audience', metric: 'clients', target: 10, closedDaysAgo: 75 }],
  }),
  makeGoal({
    id: 'demo-g-2', title: 'Golden Hour — the Q1 launch', why: 'Sealed the trajectory of the year.', world: 'aurora', createdDaysAgo: 120, completedDaysAgo: 30, stampedDaysAgo: 30,
    rings: [
      { kind: 'counter', label: 'Q1 bookings', ink: 'bookings', metric: 'bookings', target: 5, closedDaysAgo: 34 },
      { kind: 'counter', label: 'Q1 paid', ink: 'revenue', metric: 'revenue', target: 25000, closedDaysAgo: 30 },
      { kind: 'step', label: 'Launch the new site', ink: 'ops', closedDaysAgo: 48 },
    ],
  }),
  makeGoal({
    id: 'demo-g-3', title: 'A full wedding season', why: '12 booked weddings this year gets the studio to full-time.', world: 'tidewater', pinned: true, createdDaysAgo: 90, dueInDays: 200,
    rings: [
      { kind: 'counter', label: 'Weddings booked', ink: 'bookings', metric: 'bookings', target: 12, value: 8 },
      { kind: 'counter', label: 'Wedding revenue', ink: 'revenue', metric: 'revenue', target: 120000, value: 74800 },
      { kind: 'step', label: 'Book the second shooter', ink: 'ops', closedDaysAgo: 12 },
      { kind: 'step', label: 'Email last season’s couples', ink: 'audience' },
    ],
  }),
  makeGoal({
    id: 'demo-g-4', title: 'Move upmarket into brand work', why: 'Editorial and brand campaigns, not just weddings.', world: 'halcyon', pinned: true, createdDaysAgo: 60,
    rings: [
      { kind: 'counter', label: 'Brand galleries out', ink: 'craft', metric: 'galleries', target: 4, value: 3 },
      { kind: 'step', label: 'Update the pricing page', ink: 'ops', closedDaysAgo: 20 },
      { kind: 'step', label: 'Pitch Halcyon Co.', ink: 'audience' },
    ],
  }),
  makeGoal({
    id: 'demo-g-5', title: 'Protect the family weekends', why: 'Ship faster, edit less.', world: 'nightmarket', createdDaysAgo: 25,
    rings: [
      { kind: 'counter', label: 'Galleries delivered', ink: 'craft', metric: 'galleries', target: 6, value: 4 },
      { kind: 'step', label: 'Hire the retoucher', ink: 'ops' },
    ],
  }),
];

// ──────────────────────────────────────────────────────────────────
// Expenses — year's worth of mileage, equipment, travel, meals,
// software, and misc so the Expenses page's donut, breakdown bars,
// and totals all show meaningful data. Total ≈ $8,500 across the
// year. Mirrors the iOS MetaDataSeeder structure so the two
// platforms feel like the same studio.
// ──────────────────────────────────────────────────────────────────
type DemoExpense = Record<string, any>;

function makeExpense(opts: {
  id: string;
  category: 'mileage' | 'equipment' | 'travel' | 'meals' | 'software' | 'other';
  amountCents: number;
  description: string;
  daysAgo: number;
  miles?: number;
  origin?: string;
  destination?: string;
  vendor?: string;
  projectId?: string;
}): DemoExpense {
  const occurred = isoDate(-opts.daysAgo);
  return {
    id: opts.id,
    org_id: 'demo-org',
    project_id: opts.projectId ?? null,
    user_id: null,
    category: opts.category,
    amount_cents: opts.amountCents,
    currency: 'usd',
    description: opts.description,
    occurred_on: occurred,
    miles: opts.miles ?? null,
    origin: opts.origin ?? null,
    destination: opts.destination ?? null,
    vendor: opts.vendor ?? null,
    receipt_url: null,
    created_at: isoTimestamp(-opts.daysAgo, 9),
    updated_at: isoTimestamp(-opts.daysAgo, 9),
    project: opts.projectId
      ? { id: opts.projectId, name: demoProjects.find((p) => p.id === opts.projectId)?.name ?? null }
      : null,
  };
}

export const demoExpenses: DemoExpense[] = [
  // ── Mileage (12 trips scattered through the year) ──
  makeExpense({ id: 'demo-e-1',  category: 'mileage',   amountCents: 4824,  description: 'Studio to Brooklyn shoot',     daysAgo: 2,   miles: 72,  origin: 'Studio', destination: 'Brooklyn, NY',       projectId: 'demo-p-1' }),
  makeExpense({ id: 'demo-e-2',  category: 'mileage',   amountCents: 2546,  description: 'Engagement session — Topanga', daysAgo: 8,   miles: 38,  origin: 'Studio', destination: 'Topanga Canyon, CA', projectId: 'demo-p-2' }),
  makeExpense({ id: 'demo-e-3',  category: 'mileage',   amountCents: 6432,  description: 'Wedding scout day',            daysAgo: 15,  miles: 96,  origin: 'Studio', destination: 'Hudson Valley, NY',  projectId: 'demo-p-3' }),
  makeExpense({ id: 'demo-e-4',  category: 'mileage',   amountCents: 1876,  description: 'Headshot session run',         daysAgo: 22,  miles: 28,  origin: 'Studio', destination: 'Manhattan, NY' }),
  makeExpense({ id: 'demo-e-5',  category: 'mileage',   amountCents: 8442,  description: 'Brand shoot — out of town',    daysAgo: 31,  miles: 126, origin: 'Studio', destination: 'Catskills, NY',      projectId: 'demo-p-4' }),
  makeExpense({ id: 'demo-e-6',  category: 'mileage',   amountCents: 3082,  description: 'Family session venue',         daysAgo: 45,  miles: 46,  origin: 'Studio', destination: 'Westchester, NY',    projectId: 'demo-p-5' }),
  makeExpense({ id: 'demo-e-7',  category: 'mileage',   amountCents: 5360,  description: 'Property listing run',         daysAgo: 58,  miles: 80,  origin: 'Studio', destination: 'Miami, FL' }),
  makeExpense({ id: 'demo-e-8',  category: 'mileage',   amountCents: 2010,  description: 'Client meeting',               daysAgo: 67,  miles: 30,  origin: 'Studio', destination: 'Soho, NY' }),
  makeExpense({ id: 'demo-e-9',  category: 'mileage',   amountCents: 7437,  description: 'Ember pre-pro drive',          daysAgo: 82,  miles: 111, origin: 'Studio', destination: 'Bear Mountain, NY',  projectId: 'demo-p-7' }),
  makeExpense({ id: 'demo-e-10', category: 'mileage',   amountCents: 4154,  description: 'Editorial test shoot',         daysAgo: 96,  miles: 62,  origin: 'Studio', destination: 'Long Beach, NY' }),
  makeExpense({ id: 'demo-e-11', category: 'mileage',   amountCents: 2680,  description: 'Inquiry consult drive',        daysAgo: 110, miles: 40,  origin: 'Studio', destination: 'Brooklyn Heights' }),
  makeExpense({ id: 'demo-e-12', category: 'mileage',   amountCents: 5896,  description: 'Multi-day production trip',    daysAgo: 138, miles: 88,  origin: 'Studio', destination: 'Hamptons, NY' }),

  // ── Equipment ──
  makeExpense({ id: 'demo-e-13', category: 'equipment', amountCents: 189900, description: 'Sigma 35mm 1.4 Art lens',     daysAgo: 35,  vendor: 'B&H Photo' }),
  makeExpense({ id: 'demo-e-14', category: 'equipment', amountCents: 44900,  description: 'Aputure 60d LED',             daysAgo: 68,  vendor: 'Adorama' }),
  makeExpense({ id: 'demo-e-15', category: 'equipment', amountCents: 18500,  description: 'SanDisk 1TB CFexpress',       daysAgo: 100, vendor: 'Amazon' }),
  makeExpense({ id: 'demo-e-16', category: 'equipment', amountCents: 24900,  description: 'SSD enclosure + 4TB drive',   daysAgo: 164, vendor: 'OWC' }),

  // ── Travel ──
  makeExpense({ id: 'demo-e-17', category: 'travel',    amountCents: 52800,  description: 'Flight — LA wedding',         daysAgo: 52,  vendor: 'Delta' }),
  makeExpense({ id: 'demo-e-18', category: 'travel',    amountCents: 38400,  description: 'Hotel — Aspen Lodge',         daysAgo: 52,  vendor: 'Aspen Lodge' }),
  makeExpense({ id: 'demo-e-19', category: 'travel',    amountCents: 41200,  description: 'Flight — Austin headshots',   daysAgo: 118, vendor: 'American' }),

  // ── Meals ──
  makeExpense({ id: 'demo-e-20', category: 'meals',     amountCents: 8420,   description: 'Crew dinner — wedding eve',   daysAgo: 15,  vendor: 'Locale' }),
  makeExpense({ id: 'demo-e-21', category: 'meals',     amountCents: 4650,   description: 'Client lunch — Nova Coffee pitch', daysAgo: 25, vendor: 'Sant Ambroeus' }),
  makeExpense({ id: 'demo-e-22', category: 'meals',     amountCents: 3280,   description: 'Coffee meeting — Willow',     daysAgo: 41,  vendor: 'Blue Bottle' }),
  makeExpense({ id: 'demo-e-23', category: 'meals',     amountCents: 11240,  description: 'Wrap dinner — Ember',         daysAgo: 73,  vendor: 'Cosme' }),
  makeExpense({ id: 'demo-e-24', category: 'meals',     amountCents: 2410,   description: 'Client coffee — Dr. Reyes',   daysAgo: 89,  vendor: 'La Colombe' }),
  makeExpense({ id: 'demo-e-25', category: 'meals',     amountCents: 5680,   description: 'Crew breakfast — Hamptons',   daysAgo: 138, vendor: 'Estia' }),

  // ── Software ──
  makeExpense({ id: 'demo-e-26', category: 'software',  amountCents: 5999,   description: 'Adobe Creative Cloud',        daysAgo: 5,   vendor: 'Adobe' }),
  makeExpense({ id: 'demo-e-27', category: 'software',  amountCents: 1599,   description: 'Frame.io Pro',                daysAgo: 5,   vendor: 'Frame.io' }),
  makeExpense({ id: 'demo-e-28', category: 'software',  amountCents: 2399,   description: 'Capture One subscription',    daysAgo: 5,   vendor: 'Capture One' }),

  // ── Other ──
  makeExpense({ id: 'demo-e-29', category: 'other',     amountCents: 1500,   description: 'Parking — Manhattan shoot',   daysAgo: 22 }),
  makeExpense({ id: 'demo-e-30', category: 'other',     amountCents: 14800,  description: 'Gear repair — strobe head',   daysAgo: 60,  vendor: 'Profoto Service' }),
];

// ──────────────────────────────────────────────────────────────────
// Deliveries — handoff packages clients receive (Pixieset-style).
// Each delivery groups a few media items under a project. Thumbnails
// pull from Unsplash so demo screenshots look like real studio work.
// ──────────────────────────────────────────────────────────────────
type DemoDelivery = Record<string, any>;

function makeDelivery(opts: {
  id: string;
  projectId: string;
  clientName: string;
  title: string;
  slug: string;
  kind: 'photos' | 'video' | 'mixed';
  status: 'draft' | 'published' | 'expired';
  deliveredOffset: number; // days from now (negative = past)
  thumbnails: string[];
  fileCount: number;
}): DemoDelivery {
  const delivered = opts.deliveredOffset <= 0 ? isoTimestamp(opts.deliveredOffset, 14) : null;
  return {
    id: opts.id,
    org_id: 'demo-org',
    project_id: opts.projectId,
    client_name: opts.clientName,
    title: opts.title,
    slug: opts.slug,
    kind: opts.kind,
    status: opts.status,
    cover_image_url: opts.thumbnails[0],
    thumbnails: opts.thumbnails,
    file_count: opts.fileCount,
    delivered_at: delivered,
    expires_at: opts.status === 'expired' ? isoTimestamp(-5, 14) : null,
    created_at: isoTimestamp(opts.deliveredOffset - 1, 9),
    updated_at: delivered ?? isoTimestamp(opts.deliveredOffset - 1, 9),
    project: { id: opts.projectId, name: demoProjects.find((p) => p.id === opts.projectId)?.name ?? null },
  };
}

/**
 * MEDIA-2 — a delivery is the same shoot as its gallery, so it must
 * show the same frames. `demoDeliveryFrame('demo-g-1', 0)` is
 * literally `demoGalleryPhotos['demo-g-1'][0]`, which is what stops
 * the Deliveries tab and the Galleries tab from looking like two
 * different studios in the same screen recording.
 *
 * `hero` rendition (2400px WebP, ~40–120 KB): delivery thumbnails
 * render as large cards and the file is small enough to be the only
 * rendition these surfaces need.
 */
const deliveryFrame = (demoGalleryId: string, index: number): string =>
  demoPhotosFor(demoGalleryId)[index]?.urls.hero ?? '';

export const demoDeliveries: DemoDelivery[] = [
  makeDelivery({
    id: 'demo-d-1',
    projectId: 'demo-p-3',
    clientName: 'Kayla + James',
    title: 'Kayla + James — Wedding Gallery',
    slug: 'kayla-james-wedding',
    kind: 'mixed',
    status: 'published',
    deliveredOffset: -8,
    thumbnails: [
      deliveryFrame('demo-g-1', 0),
      deliveryFrame('demo-g-1', 1),
      deliveryFrame('demo-g-1', 2),
      deliveryFrame('demo-g-1', 3),
    ],
    fileCount: 412,
  }),
  makeDelivery({
    id: 'demo-d-2',
    projectId: 'demo-p-4',
    clientName: 'Nova Coffee Co',
    title: 'Nova Coffee — Brand Campaign Selects',
    slug: 'nova-coffee-q2',
    kind: 'photos',
    status: 'published',
    deliveredOffset: -3,
    thumbnails: [
      deliveryFrame('demo-g-2', 0),
      deliveryFrame('demo-g-2', 1),
      deliveryFrame('demo-g-2', 2),
    ],
    fileCount: 86,
  }),
  makeDelivery({
    id: 'demo-d-3',
    projectId: 'demo-p-5',
    clientName: 'Hollins Family',
    title: 'Hollins Family — Spring Session',
    slug: 'hollins-family-session',
    kind: 'photos',
    status: 'published',
    deliveredOffset: -1,
    thumbnails: [
      deliveryFrame('demo-g-4', 0),
      deliveryFrame('demo-g-4', 1),
      deliveryFrame('demo-g-4', 2),
    ],
    fileCount: 64,
  }),
  makeDelivery({
    id: 'demo-d-4',
    projectId: 'demo-p-11',
    clientName: 'Nova Coffee Co',
    title: 'Nova Coffee — Q1 Spot (Final Cut)',
    slug: 'nova-q1-spot',
    kind: 'video',
    status: 'published',
    deliveredOffset: -28,
    thumbnails: [
      deliveryFrame('demo-g-2', 6),
      deliveryFrame('demo-g-2', 7),
    ],
    fileCount: 3,
  }),
  makeDelivery({
    id: 'demo-d-5',
    projectId: 'demo-p-12',
    clientName: 'Dr. Alicia Reyes',
    title: 'Dr. Reyes — Headshot Finals',
    slug: 'reyes-headshot-finals',
    kind: 'photos',
    status: 'expired',
    deliveredOffset: -90,
    thumbnails: [
      deliveryFrame('demo-g-5', 0),
      deliveryFrame('demo-g-5', 1),
    ],
    fileCount: 3,
  }),
  makeDelivery({
    id: 'demo-d-6',
    projectId: 'demo-p-8',
    clientName: 'The Chen Family',
    title: 'Chen — Editorial Selects (In Progress)',
    slug: 'chen-editorial-selects',
    kind: 'photos',
    status: 'draft',
    deliveredOffset: 4,
    thumbnails: [
      deliveryFrame('demo-g-3', 2),
      deliveryFrame('demo-g-3', 3),
    ],
    fileCount: 22,
  }),
];

// ──────────────────────────────────────────────────────────────────
// Demo galleries — populate the redesigned Galleries surface (#301)
// so demo-mode studios see a real photo masonry instead of an empty
// state. Mirrors the StudioGallery shape from `useStudioGalleries`
// so the hook can return these without a separate adapter when
// isDemo is on. Cover photos pulled from Unsplash with stable IDs
// so screenshot builds reproduce identically. Added 2026-06-18 (#317).
// ──────────────────────────────────────────────────────────────────
export const demoGalleries = [
  {
    id: 'demo-g-1',
    project_id: 'demo-p-3',
    name: 'Kayla + James Wedding',
    description: 'Full day coverage · Hudson Valley',
    is_public: true,
    download_enabled: true,
    proofing_enabled: true,
    proofing_locked: false,
    cover_photo_id: null,
    created_at: new Date(now.getTime() - 8 * dayMs).toISOString(),
    updated_at: new Date(now.getTime() - 6 * dayMs).toISOString(),
    project_name: 'Kayla + James — Wedding Edit',
    client_name: 'Kayla + James',
    cover_url: demoGalleryCovers['demo-g-1'].urls.hero,
    photo_count: 24,
  },
  {
    id: 'demo-g-2',
    project_id: 'demo-p-4',
    name: 'Nova Coffee — Brand Campaign',
    description: 'Q2 selects · studio + on-location',
    is_public: false,
    download_enabled: false,
    proofing_enabled: true,
    proofing_locked: false,
    cover_photo_id: null,
    created_at: new Date(now.getTime() - 3 * dayMs).toISOString(),
    updated_at: new Date(now.getTime() - 1 * dayMs).toISOString(),
    project_name: 'Nova Coffee — Brand Shoot',
    client_name: 'Nova Coffee Co',
    cover_url: demoGalleryCovers['demo-g-2'].urls.hero,
    photo_count: 18,
  },
  {
    id: 'demo-g-3',
    project_id: 'demo-p-2',
    name: 'Marcus & Elle Engagement',
    description: 'Golden hour, downtown',
    is_public: true,
    download_enabled: true,
    proofing_enabled: false,
    proofing_locked: false,
    cover_photo_id: null,
    created_at: new Date(now.getTime() - 14 * dayMs).toISOString(),
    updated_at: new Date(now.getTime() - 10 * dayMs).toISOString(),
    project_name: 'Marcus & Elle — Engagement',
    client_name: 'Marcus & Elle',
    cover_url: demoGalleryCovers['demo-g-3'].urls.hero,
    photo_count: 16,
  },
  {
    id: 'demo-g-4',
    project_id: 'demo-p-5',
    name: 'Hollins Family — Spring Set',
    description: 'Family portrait session',
    is_public: false,
    download_enabled: false,
    proofing_enabled: false,
    proofing_locked: false,
    cover_photo_id: null,
    created_at: new Date(now.getTime() - 22 * dayMs).toISOString(),
    updated_at: new Date(now.getTime() - 20 * dayMs).toISOString(),
    project_name: 'Hollins Family Session',
    client_name: 'Hollins Family',
    cover_url: demoGalleryCovers['demo-g-4'].urls.hero,
    photo_count: 22,
  },
  {
    id: 'demo-g-5',
    project_id: 'demo-p-12',
    name: 'Dr. Reyes — Headshots',
    description: 'Press kit refresh',
    is_public: true,
    download_enabled: true,
    proofing_enabled: false,
    proofing_locked: false,
    cover_photo_id: null,
    created_at: new Date(now.getTime() - 35 * dayMs).toISOString(),
    updated_at: new Date(now.getTime() - 30 * dayMs).toISOString(),
    project_name: 'Dr. Reyes — Headshot',
    client_name: 'Dr. Alicia Reyes',
    cover_url: demoGalleryCovers['demo-g-5'].urls.hero,
    photo_count: 12,
  },
];

// ──────────────────────────────────────────────────────────────────
// Delivery files — the individual media items inside a delivery.
// Videos use Mux's documented public playback IDs so the player
// renders in Preview Studio without needing real assets. Photos
// pull from Unsplash. Timecode comments (below) reference these
// video ids.
//
// Mux public sample ids sourced from
// https://docs.mux.com/guides/video/upload-files-directly#test-video
// — these are safe to embed in demos.
// ──────────────────────────────────────────────────────────────────
type DemoDeliveryFile = Record<string, any>;

function makeDeliveryFile(opts: {
  id: string;
  deliveryId: string;
  name: string;
  kind: 'photo' | 'video';
  url: string;
  posterUrl?: string;
  muxPlaybackId?: string;
  durationSeconds?: number;
  sortOrder: number;
  daysAgo: number;
}): DemoDeliveryFile {
  const uploaded = isoTimestamp(-opts.daysAgo, 14);
  return {
    id: opts.id,
    delivery_id: opts.deliveryId,
    name: opts.name,
    kind: opts.kind,
    url: opts.url,
    poster_url: opts.posterUrl ?? null,
    mux_playback_id: opts.muxPlaybackId ?? null,
    duration_seconds: opts.durationSeconds ?? null,
    sort_order: opts.sortOrder,
    created_at: uploaded,
    updated_at: uploaded,
  };
}

// Mux public sample playback ids — the "big buck bunny" preview and
// the Mux marketing sample. These render live in the demo player.
const MUX_SAMPLE_1 = 'DS00Spx1CV902MCtPj5WknGlR102V5HFkDe';
const MUX_SAMPLE_2 = 'qxb01i6T202xr01Ln9jZQooBhbEDzsMr8IhtCyerY01P00';

export const demoDeliveryFiles: DemoDeliveryFile[] = [
  // Wedding gallery (demo-d-1) — 6 photos + 1 highlight film
  makeDeliveryFile({ id: 'demo-df-1',  deliveryId: 'demo-d-1', name: '01-ceremony-first-kiss.jpg', kind: 'photo', url: deliveryFrame('demo-g-1', 0), sortOrder: 1, daysAgo: 8 }),
  makeDeliveryFile({ id: 'demo-df-2',  deliveryId: 'demo-d-1', name: '02-details-rings.jpg',      kind: 'photo', url: deliveryFrame('demo-g-1', 1), sortOrder: 2, daysAgo: 8 }),
  makeDeliveryFile({ id: 'demo-df-3',  deliveryId: 'demo-d-1', name: '03-portrait-couple.jpg',    kind: 'photo', url: deliveryFrame('demo-g-1', 2), sortOrder: 3, daysAgo: 8 }),
  makeDeliveryFile({ id: 'demo-df-4',  deliveryId: 'demo-d-1', name: '04-reception-toast.jpg',    kind: 'photo', url: deliveryFrame('demo-g-1', 3), sortOrder: 4, daysAgo: 8 }),
  makeDeliveryFile({ id: 'demo-df-5',  deliveryId: 'demo-d-1', name: '05-first-dance.jpg',        kind: 'photo', url: deliveryFrame('demo-g-1', 4), sortOrder: 5, daysAgo: 8 }),
  makeDeliveryFile({ id: 'demo-df-6',  deliveryId: 'demo-d-1', name: '06-golden-hour.jpg',        kind: 'photo', url: deliveryFrame('demo-g-1', 5), sortOrder: 6, daysAgo: 8 }),
  makeDeliveryFile({
    id: 'demo-df-7', deliveryId: 'demo-d-1', name: 'kayla-james-highlight-film.mp4', kind: 'video',
    url: `https://stream.mux.com/${MUX_SAMPLE_1}.m3u8`,
    posterUrl: `https://image.mux.com/${MUX_SAMPLE_1}/thumbnail.jpg?time=4`,
    muxPlaybackId: MUX_SAMPLE_1,
    durationSeconds: 187,
    sortOrder: 7, daysAgo: 8,
  }),

  // Nova Coffee brand shoot (demo-d-2) — 4 selects
  makeDeliveryFile({ id: 'demo-df-8',  deliveryId: 'demo-d-2', name: 'campaign-hero.jpg',      kind: 'photo', url: deliveryFrame('demo-g-2', 0), sortOrder: 1, daysAgo: 3 }),
  makeDeliveryFile({ id: 'demo-df-9',  deliveryId: 'demo-d-2', name: 'product-detail.jpg',    kind: 'photo', url: deliveryFrame('demo-g-2', 1), sortOrder: 2, daysAgo: 3 }),
  makeDeliveryFile({ id: 'demo-df-10', deliveryId: 'demo-d-2', name: 'lifestyle-01.jpg',      kind: 'photo', url: deliveryFrame('demo-g-2', 2), sortOrder: 3, daysAgo: 3 }),
  makeDeliveryFile({ id: 'demo-df-11', deliveryId: 'demo-d-2', name: 'lifestyle-02.jpg',      kind: 'photo', url: deliveryFrame('demo-g-2', 3), sortOrder: 4, daysAgo: 3 }),

  // Hollins family (demo-d-3) — 3 photos
  makeDeliveryFile({ id: 'demo-df-12', deliveryId: 'demo-d-3', name: 'family-portrait.jpg',   kind: 'photo', url: deliveryFrame('demo-g-4', 0), sortOrder: 1, daysAgo: 1 }),
  makeDeliveryFile({ id: 'demo-df-13', deliveryId: 'demo-d-3', name: 'siblings.jpg',           kind: 'photo', url: deliveryFrame('demo-g-4', 1), sortOrder: 2, daysAgo: 1 }),
  makeDeliveryFile({ id: 'demo-df-14', deliveryId: 'demo-d-3', name: 'candid-play.jpg',        kind: 'photo', url: deliveryFrame('demo-g-4', 2), sortOrder: 3, daysAgo: 1 }),

  // Nova Q1 spot (demo-d-4) — final cut video + 2 stills
  makeDeliveryFile({
    id: 'demo-df-15', deliveryId: 'demo-d-4', name: 'nova-q1-final-cut.mp4', kind: 'video',
    url: `https://stream.mux.com/${MUX_SAMPLE_2}.m3u8`,
    posterUrl: `https://image.mux.com/${MUX_SAMPLE_2}/thumbnail.jpg?time=2`,
    muxPlaybackId: MUX_SAMPLE_2,
    durationSeconds: 62,
    sortOrder: 1, daysAgo: 28,
  }),
  makeDeliveryFile({ id: 'demo-df-16', deliveryId: 'demo-d-4', name: 'still-frame-01.jpg',    kind: 'photo', url: deliveryFrame('demo-g-2', 6), sortOrder: 2, daysAgo: 28 }),
  makeDeliveryFile({ id: 'demo-df-17', deliveryId: 'demo-d-4', name: 'still-frame-02.jpg',    kind: 'photo', url: deliveryFrame('demo-g-2', 7), sortOrder: 3, daysAgo: 28 }),

  // Dr. Reyes headshot finals (demo-d-5) — 3 portraits
  makeDeliveryFile({ id: 'demo-df-18', deliveryId: 'demo-d-5', name: 'reyes-headshot-01.jpg', kind: 'photo', url: deliveryFrame('demo-g-5', 0), sortOrder: 1, daysAgo: 90 }),
  makeDeliveryFile({ id: 'demo-df-21', deliveryId: 'demo-d-5', name: 'reyes-headshot-02.jpg', kind: 'photo', url: deliveryFrame('demo-g-5', 1), sortOrder: 2, daysAgo: 90 }),
  makeDeliveryFile({ id: 'demo-df-22', deliveryId: 'demo-d-5', name: 'reyes-headshot-03.jpg', kind: 'photo', url: deliveryFrame('demo-g-5', 2),    sortOrder: 3, daysAgo: 90 }),

  // Chen editorial draft (demo-d-6) — 2 photos + 1 test video
  // (mirrors iOS DemoSeed's editorial-test.mp4 so the review panel
  // demos video on both platforms)
  makeDeliveryFile({ id: 'demo-df-19', deliveryId: 'demo-d-6', name: 'editorial-01.jpg',      kind: 'photo', url: deliveryFrame('demo-g-3', 2), sortOrder: 1, daysAgo: 2 }),
  makeDeliveryFile({ id: 'demo-df-20', deliveryId: 'demo-d-6', name: 'editorial-02.jpg',      kind: 'photo', url: deliveryFrame('demo-g-3', 3), sortOrder: 2, daysAgo: 2 }),
  makeDeliveryFile({
    id: 'demo-df-23', deliveryId: 'demo-d-6', name: 'editorial-test.mp4', kind: 'video',
    url: `https://stream.mux.com/${MUX_SAMPLE_1}.m3u8`,
    posterUrl: `https://image.mux.com/${MUX_SAMPLE_1}/thumbnail.jpg?time=10`,
    muxPlaybackId: MUX_SAMPLE_1,
    durationSeconds: 187,
    sortOrder: 3, daysAgo: 2,
  }),
];

// ──────────────────────────────────────────────────────────────────
// Delivery video comments — timecode feedback on the two demo
// videos. Mix of resolved / unresolved with plausible author names
// so the review panel looks like a real client conversation.
// ──────────────────────────────────────────────────────────────────
type DemoDeliveryVideoComment = Record<string, any>;

function makeVideoComment(opts: {
  id: string;
  fileId: string;
  deliveryId: string;
  timecode: number | null;
  body: string;
  authorName: string;
  daysAgo: number;
  resolved?: boolean;
  parentId?: string;
}): DemoDeliveryVideoComment {
  const createdAt = isoTimestamp(-opts.daysAgo, 15);
  return {
    id: opts.id,
    delivery_id: opts.deliveryId,
    delivery_file_id: opts.fileId,
    timecode_seconds: opts.timecode,
    body: opts.body,
    author_name: opts.authorName,
    author_email: null,
    parent_id: opts.parentId ?? null,
    resolved: opts.resolved ?? false,
    resolved_at: opts.resolved ? isoTimestamp(-Math.max(opts.daysAgo - 1, 0), 16) : null,
    resolved_by: opts.resolved ? 'demo-studio-user' : null,
    created_at: createdAt,
  };
}

export const demoDeliveryVideoComments: DemoDeliveryVideoComment[] = [
  // Kayla + James highlight film (demo-df-7)
  makeVideoComment({ id: 'demo-vc-1', fileId: 'demo-df-7', deliveryId: 'demo-d-1', timecode: 12,  body: "The vows section is perfect — can we hold this shot a beat longer?", authorName: 'Kayla', daysAgo: 3, resolved: true }),
  makeVideoComment({ id: 'demo-vc-2', fileId: 'demo-df-7', deliveryId: 'demo-d-1', timecode: 34,  body: "Love the golden-hour cut — this is the one for socials.", authorName: 'James', daysAgo: 3, resolved: true }),
  makeVideoComment({ id: 'demo-vc-3', fileId: 'demo-df-7', deliveryId: 'demo-d-1', timecode: 78,  body: "Can we soften the audio on Dad's speech? It clips a bit.", authorName: 'Kayla', daysAgo: 2, resolved: false }),
  makeVideoComment({ id: 'demo-vc-4', fileId: 'demo-df-7', deliveryId: 'demo-d-1', timecode: 121, body: "Please swap in one more reception dance shot around here.", authorName: 'James', daysAgo: 1, resolved: false }),
  makeVideoComment({ id: 'demo-vc-5', fileId: 'demo-df-7', deliveryId: 'demo-d-1', timecode: null, body: "Overall this is beyond what we imagined. Thank you.", authorName: 'Kayla', daysAgo: 1, resolved: false }),

  // Nova Q1 spot (demo-df-15)
  makeVideoComment({ id: 'demo-vc-6',  fileId: 'demo-df-15', deliveryId: 'demo-d-4', timecode: 4,  body: "Logo needs another half-second to breathe here.", authorName: 'Amelia · Nova Coffee', daysAgo: 30, resolved: true }),
  makeVideoComment({ id: 'demo-vc-7',  fileId: 'demo-df-15', deliveryId: 'demo-d-4', timecode: 22, body: "Product hero looks stunning — approved.", authorName: 'Amelia · Nova Coffee', daysAgo: 30, resolved: true }),
  makeVideoComment({ id: 'demo-vc-8',  fileId: 'demo-df-15', deliveryId: 'demo-d-4', timecode: 41, body: "Can we boost saturation slightly on the model close-up?", authorName: 'Amelia · Nova Coffee', daysAgo: 29, resolved: true }),
  makeVideoComment({ id: 'demo-vc-9',  fileId: 'demo-df-15', deliveryId: 'demo-d-4', timecode: 55, body: "Perfect ending. This one's ready to ship.", authorName: 'Amelia · Nova Coffee', daysAgo: 29, resolved: true }),
  makeVideoComment({ id: 'demo-vc-10', fileId: 'demo-df-15', deliveryId: 'demo-d-4', timecode: null, body: "Legendary work — client loved it.", authorName: 'Amelia · Nova Coffee', daysAgo: 28, resolved: true }),
];

// ──────────────────────────────────────────────────────────────────
// Calendar events — mix of shoots, consultations, editing blocks,
// crew calls across last month + next month so the calendar page
// isn't a wasteland when Preview Studio is on. Each event optionally
// links back to a project so the client-context chip renders.
// ──────────────────────────────────────────────────────────────────
type DemoCalendarEvent = Record<string, any>;

function makeCalendarEvent(opts: {
  id: string;
  title: string;
  description?: string | null;
  daysAway: number; // negative = past
  startHour?: number;
  durationHours?: number;
  allDay?: boolean;
  location?: string | null;
  projectId?: string | null;
  color?: string | null;
  meetingUrl?: string | null;
  imageUrl?: string | null;
}): DemoCalendarEvent {
  const startAt = new Date(now.getTime() + opts.daysAway * dayMs);
  startAt.setHours(opts.startHour ?? 10, 0, 0, 0);
  const endAt = new Date(startAt.getTime() + (opts.durationHours ?? 2) * 60 * 60 * 1000);
  const createdAt = new Date(now.getTime() - Math.abs(opts.daysAway + 3) * dayMs);
  return {
    id: opts.id,
    org_id: 'demo-org',
    title: opts.title,
    description: opts.description ?? null,
    start_at: startAt.toISOString(),
    end_at: endAt.toISOString(),
    all_day: opts.allDay ?? false,
    location: opts.location ?? null,
    project_id: opts.projectId ?? null,
    color: opts.color ?? null,
    image_url: opts.imageUrl ?? null,
    meeting_url: opts.meetingUrl ?? null,
    created_by: 'demo-studio-user',
    created_at: createdAt.toISOString(),
    updated_at: createdAt.toISOString(),
  };
}

export const demoCalendarEvents: DemoCalendarEvent[] = [
  // Last month
  makeCalendarEvent({ id: 'demo-ce-1',  title: 'Kayla + James — Wedding Day', description: 'Full-day coverage · 2 shooters', daysAway: -10, startHour: 11, durationHours: 10, location: 'Hudson Valley, NY', projectId: 'demo-p-3', color: '#977726' }),
  makeCalendarEvent({ id: 'demo-ce-2',  title: 'Nova Coffee — Brand Shoot', description: 'Studio · full team', daysAway: -8, startHour: 9, durationHours: 8, location: 'Brooklyn studio', projectId: 'demo-p-4', color: '#CF4A72' }),
  makeCalendarEvent({ id: 'demo-ce-3',  title: 'Hollins Family Session', description: 'In-home newborn + toddler', daysAway: -3, startHour: 15, durationHours: 2, location: 'Hollins residence', projectId: 'demo-p-5', color: '#368C50' }),
  makeCalendarEvent({ id: 'demo-ce-4',  title: 'Editing block — Kayla + James wedding', daysAway: -6, startHour: 10, durationHours: 4, projectId: 'demo-p-3', color: '#477AD1' }),
  makeCalendarEvent({ id: 'demo-ce-5',  title: 'Editing block — Nova Q2 selects', daysAway: -4, startHour: 14, durationHours: 3, projectId: 'demo-p-4', color: '#477AD1' }),

  // Today
  makeCalendarEvent({ id: 'demo-ce-6',  title: 'Sarah & Ben — Wedding Day', description: 'Full-day coverage · Aspen Lodge', daysAway: 0, startHour: 12, durationHours: 10, location: 'Aspen Lodge, CO', projectId: 'demo-p-1', color: '#977726' }),
  makeCalendarEvent({ id: 'demo-ce-7',  title: 'Marcus & Elle — Engagement', description: 'Golden-hour rooftop shoot', daysAway: 0, startHour: 18, durationHours: 2, location: 'Downtown rooftop', projectId: 'demo-p-2', color: '#BC942F' }),

  // Next 30 days
  makeCalendarEvent({ id: 'demo-ce-8',  title: 'Consultation — Riley Cohen', description: 'Wedding · Sept 2026', daysAway: 2, startHour: 11, durationHours: 1, meetingUrl: 'https://meet.google.com/demo-riley', color: '#2E84A3' }),
  makeCalendarEvent({ id: 'demo-ce-9',  title: 'Consultation — Maya Patel', description: 'Brand video · skincare launch', daysAway: 3, startHour: 15, durationHours: 1, meetingUrl: 'https://meet.google.com/demo-maya', color: '#2E84A3' }),
  makeCalendarEvent({ id: 'demo-ce-10', title: 'Chen — Editorial shoot', daysAway: 4, startHour: 10, durationHours: 6, location: 'Long Island City', projectId: 'demo-p-8', color: '#977726' }),
  makeCalendarEvent({ id: 'demo-ce-11', title: 'Editing block — Sarah & Ben wedding', daysAway: 6, startHour: 9, durationHours: 5, projectId: 'demo-p-1', color: '#477AD1' }),
  makeCalendarEvent({ id: 'demo-ce-12', title: 'Sunset Kids — Family Session', daysAway: 9, startHour: 17, durationHours: 2, location: 'Piedmont Park, ATL', projectId: 'demo-p-10', color: '#BC942F' }),
  makeCalendarEvent({ id: 'demo-ce-13', title: 'Willow Boutique — Scout day', daysAway: 12, startHour: 10, durationHours: 4, location: 'Willow Boutique HQ', projectId: 'demo-p-9', color: '#9666CC' }),
  makeCalendarEvent({ id: 'demo-ce-14', title: 'Ember — Pre-production meeting', daysAway: 15, startHour: 14, durationHours: 2, meetingUrl: 'https://zoom.us/demo/ember', projectId: 'demo-p-7', color: '#9666CC' }),
  makeCalendarEvent({ id: 'demo-ce-15', title: 'Andersson — Venue walkthrough', daysAway: 21, startHour: 13, durationHours: 2, location: 'Wythe Hotel, Brooklyn', projectId: 'demo-p-6', color: '#2E84A3' }),
];

// ──────────────────────────────────────────────────────────────────
// Booking links — public scheduling pages the studio hands to
// prospective clients ("book a discovery call"). Each has an active
// slug + a small handful of available/upcoming slots.
// ──────────────────────────────────────────────────────────────────
type DemoBookingLink = Record<string, any>;

export const demoBookingLinks: DemoBookingLink[] = [
  {
    id: 'demo-bl-1',
    org_id: 'demo-org',
    slug: 'discovery-call',
    name: 'Discovery Call',
    description: 'A 30-minute intro chat about your project.',
    duration_minutes: 30,
    color: '#977726',
    is_active: true,
    booking_window_days: 45,
    buffer_minutes: 15,
    location: 'Google Meet — link sent on confirmation',
    created_at: isoTimestamp(-45),
    updated_at: isoTimestamp(-2),
  },
  {
    id: 'demo-bl-2',
    org_id: 'demo-org',
    slug: 'wedding-consult',
    name: 'Wedding Consultation',
    description: 'An hour walkthrough of your day, timeline, and coverage options.',
    duration_minutes: 60,
    color: '#CF4A72',
    is_active: true,
    booking_window_days: 60,
    buffer_minutes: 30,
    location: 'In-person or video',
    created_at: isoTimestamp(-45),
    updated_at: isoTimestamp(-5),
  },
  {
    id: 'demo-bl-3',
    org_id: 'demo-org',
    slug: 'brand-strategy',
    name: 'Brand Strategy Session',
    description: 'For agencies + in-house creative teams. Scope + pricing.',
    duration_minutes: 45,
    color: '#9666CC',
    is_active: true,
    booking_window_days: 30,
    buffer_minutes: 15,
    location: 'Video call',
    created_at: isoTimestamp(-45),
    updated_at: isoTimestamp(-10),
  },
];

// ──────────────────────────────────────────────────────────────────
// Payments / Invoices — the money surface. Mixed statuses (paid,
// sent, overdue, draft) across the current client roster so the
// revenue ribbons look alive. Uses the invoices table shape.
// ──────────────────────────────────────────────────────────────────
type DemoInvoice = Record<string, any>;

function makeInvoice(opts: {
  id: string;
  projectId: string;
  amount: number; // dollars
  status: 'draft' | 'sent' | 'paid' | 'overdue';
  daysAgo: number;
  dueDaysOffset?: number; // relative to now; default = +14 from created
  description?: string;
}): DemoInvoice {
  const createdAt = isoTimestamp(-opts.daysAgo, 10);
  const dueDate = isoDate(opts.dueDaysOffset ?? -opts.daysAgo + 14);
  const paidAt = opts.status === 'paid' ? isoTimestamp(-Math.max(opts.daysAgo - 3, 0), 11) : null;
  return {
    id: opts.id,
    project_id: opts.projectId,
    amount: opts.amount,
    amount_cents: Math.round(opts.amount * 100),
    due_date: dueDate,
    status: opts.status,
    file_url: null,
    stripe_invoice_id: null,
    description: opts.description ?? null,
    paid_at: paidAt,
    created_at: createdAt,
    updated_at: paidAt ?? createdAt,
  };
}

export const demoInvoices: DemoInvoice[] = [
  // Paid — recent
  makeInvoice({ id: 'demo-inv-1',  projectId: 'demo-p-3',  amount: 3100, status: 'paid',  daysAgo: 30, description: 'Wedding · deposit' }),
  makeInvoice({ id: 'demo-inv-2',  projectId: 'demo-p-3',  amount: 3100, status: 'paid',  daysAgo: 6,  description: 'Wedding · balance' }),
  makeInvoice({ id: 'demo-inv-3',  projectId: 'demo-p-4',  amount: 1100, status: 'paid',  daysAgo: 12, description: 'Nova Coffee · deposit' }),
  makeInvoice({ id: 'demo-inv-4',  projectId: 'demo-p-5',  amount: 850,  status: 'paid',  daysAgo: 4,  description: 'Hollins family session' }),
  makeInvoice({ id: 'demo-inv-5',  projectId: 'demo-p-11', amount: 3200, status: 'paid',  daysAgo: 55, description: 'Nova Q1 spot' }),
  makeInvoice({ id: 'demo-inv-6',  projectId: 'demo-p-12', amount: 2400, status: 'paid',  daysAgo: 50, description: 'Reyes headshots' }),

  // Sent — awaiting payment
  makeInvoice({ id: 'demo-inv-7',  projectId: 'demo-p-1',  amount: 2400, status: 'sent',  daysAgo: 5, dueDaysOffset: 9,  description: 'Sarah & Ben · balance' }),
  makeInvoice({ id: 'demo-inv-8',  projectId: 'demo-p-4',  amount: 1100, status: 'sent',  daysAgo: 2, dueDaysOffset: 12, description: 'Nova Coffee · balance' }),
  makeInvoice({ id: 'demo-inv-9',  projectId: 'demo-p-8',  amount: 1900, status: 'sent',  daysAgo: 4, dueDaysOffset: 10, description: 'Chen · deposit' }),

  // Overdue — needs a nudge
  makeInvoice({ id: 'demo-inv-10', projectId: 'demo-p-6',  amount: 3700, status: 'overdue', daysAgo: 30, dueDaysOffset: -5, description: 'Andersson · deposit' }),

  // Drafts — studio hasn't sent yet
  makeInvoice({ id: 'demo-inv-11', projectId: 'demo-p-7',  amount: 4600, status: 'draft', daysAgo: 1, dueDaysOffset: 30, description: 'Ember · deposit' }),
  makeInvoice({ id: 'demo-inv-12', projectId: 'demo-p-9',  amount: 2200, status: 'draft', daysAgo: 0, dueDaysOffset: 30, description: 'Willow · deposit' }),

  // A little more paid history for the year-over-year charts
  makeInvoice({ id: 'demo-inv-13', projectId: 'demo-p-11', amount: 1600, status: 'paid', daysAgo: 80,  description: 'Nova Q1 · add-on' }),
  makeInvoice({ id: 'demo-inv-14', projectId: 'demo-p-12', amount: 900,  status: 'paid', daysAgo: 95,  description: 'Reyes · retouch pass' }),
  makeInvoice({ id: 'demo-inv-15', projectId: 'demo-p-11', amount: 2100, status: 'paid', daysAgo: 125, description: 'Nova retainer · Q4' }),
];

// ──────────────────────────────────────────────────────────────────
// Photos — populate the redesigned Galleries masonry when isDemo
// is on. Each entry mirrors the studio_photos row shape used by
// useGalleryPhotos, and includes `ai_tags` + `ai_dominant_colors`
// (Wave 5 auto-tag output) so the tag filter bar has real chips.
// ──────────────────────────────────────────────────────────────────
type DemoPhoto = Record<string, any>;

function makePhoto(opts: {
  id: string;
  galleryId: string;
  url: string;
  /** 800px-long-edge tile. See MEDIA-2 note above `generatePhotos`. */
  thumbnailUrl?: string;
  name: string;
  sortOrder: number;
  width?: number;
  height?: number;
  aiTags?: string[];
  aiColors?: string[];
  isFavorite?: boolean;
  /** MEDIA-2 — manifest id (`<sourceGallery>/<slug>`), for parity checks. */
  slug?: string;
  alt?: string;
  renditions?: Record<string, string>;
}): DemoPhoto {
  const width = opts.width ?? 1600;
  const height = opts.height ?? 1067;
  return {
    id: opts.id,
    gallery_id: opts.galleryId,
    // Both spellings: the GalleryPhoto row shape (and the justified
    // grid in GalleryEditor) read `file_url`; legacy demo consumers
    // read `url`. Missing `file_url` used to render broken tiles in
    // Preview Studio.
    url: opts.url,
    file_url: opts.url,
    thumbnail_url: opts.thumbnailUrl ?? `${opts.url.split('?')[0]}?w=400`,
    name: opts.name,
    width,
    height,
    // Deterministic pseudo-random size so screenshot builds reproduce.
    size_bytes: 340000 + ((opts.sortOrder * 37211) % 200000),
    sort_order: opts.sortOrder,
    ai_tags: opts.aiTags ?? null,
    ai_dominant_colors: opts.aiColors ?? null,
    is_favorite: opts.isFavorite ?? false,
    is_hidden: false,
    approval_state: null,
    created_at: isoTimestamp(-5, 12),
    updated_at: isoTimestamp(-5, 12),
    // MEDIA-2 extras — additive, so every existing consumer that
    // reads the studio_photos row shape is untouched.
    media_id: opts.slug ?? null,
    alt: opts.alt ?? null,
    renditions: opts.renditions ?? null,
  };
}

// MEDIA-2 — the Unsplash pools that used to live here are gone. Every
// demo frame is now one of Rennan's own, from `@/data/demoPhotos`
// (generated from the MEDIA-1 manifest; see that file's header).
//
// WHICH RENDITION GOES WHERE. These files are already encoded at
// their target sizes, so the point is to hand each surface the size
// it actually draws rather than let `src/lib/imageTransform.ts`
// re-transform a file that's already right:
//   thumbnail_url → `grid`     800px long edge, ~9–30 KB. What
//                              ClientGalleryView's grid renders
//                              directly, and what JustifiedGallery
//                              tiles want (they draw 110–300px tall).
//   url/file_url  → `hero`     2400px long edge, ~40–120 KB. The
//                              largest thing we serve, so the
//                              lightbox and the full-bleed
//                              PublicGallery cover both look right.
//                              If Supabase's paid transform add-on
//                              IS enabled, `gridUrl(file_url)` still
//                              narrows it further; if it is NOT, the
//                              fallback is a 40 KB WebP rather than
//                              MEDIA-1's feared 24 MB original.
// The keynote demo runs on hotel wifi — that trade is deliberate.
const PHOTO_TAGS: Record<string, { tags: string[]; colors: string[] }> = {
  'demo-g-1': { tags: ['portrait', 'ceremony', 'reception', 'wedding'], colors: ['#F5E6D3', '#D4AF7A', '#8C6A48'] },
  'demo-g-2': { tags: ['product', 'brand', 'lifestyle', 'studio'],      colors: ['#FADFDA', '#D77E7E', '#5B2222'] },
  'demo-g-3': { tags: ['portrait', 'engagement', 'golden-hour'],        colors: ['#F8CFAA', '#E67E3F', '#7A3A18'] },
  'demo-g-4': { tags: ['family', 'children', 'candid', 'lifestyle'],    colors: ['#E4EDD3', '#7DA35A', '#3B5232'] },
  'demo-g-5': { tags: ['portrait', 'headshot', 'editorial'],            colors: ['#EDE7DB', '#A08A6A', '#4C3F2A'] },
};

function generatePhotos(galleryId: string): DemoPhoto[] {
  const family = PHOTO_TAGS[galleryId] ?? { tags: [], colors: [] };
  return demoPhotosFor(galleryId).map((photo, i) =>
    makePhoto({
      // Photo ids stay in DEMO-1's `demo-g-1-photo-1` shape: they're
      // the keys DemoStoreContext clones and the proofing/favorite
      // state is keyed off. The manifest id rides along as `slug`
      // (below) so web and iOS can be proven to hold the same frame.
      id: `${galleryId}-photo-${i + 1}`,
      galleryId,
      url: photo.urls.hero,
      thumbnailUrl: photo.urls.grid,
      name: `${String(i + 1).padStart(3, '0')}.jpg`,
      sortOrder: i + 1,
      width: photo.width,
      height: photo.height,
      aiTags: family.tags,
      aiColors: family.colors,
      isFavorite: i === 0 || i === 4,
      // MEDIA-2 provenance — the manifest id + the other renditions,
      // so a surface that wants the 2000px lightbox or the JPEG
      // download doesn't have to rebuild the URL by hand.
      slug: photo.id,
      alt: photo.alt,
      renditions: photo.urls,
    }),
  );
}

// The masonry surfaces call `useGalleryPhotos(galleryId)`. That hook
// already checks isDemo; expose a lookup here that yields plausible
// photo sets seeded per gallery.
export const demoGalleryPhotos: Record<string, DemoPhoto[]> = {
  'demo-g-1': generatePhotos('demo-g-1'),
  'demo-g-2': generatePhotos('demo-g-2'),
  'demo-g-3': generatePhotos('demo-g-3'),
  'demo-g-4': generatePhotos('demo-g-4'),
  'demo-g-5': generatePhotos('demo-g-5'),
};

// ──────────────────────────────────────────────────────────────────
// DEMO-4 — gallery proofing state (client picks + photo comments).
//
// The keynote loop is: publish a gallery → open the client-facing
// page → heart a few frames → come back and the studio sees the
// picks. That last beat only lands if the studio side has somewhere
// to READ picks from in Preview Studio, so these seed the store and
// live picks made during the demo append to them.
//
// Seeded picks live on `demo-g-1` (Kayla + James — already delivered)
// so the Proofing canvas is never an empty state. The gallery the
// founder actually walks on stage starts clean, so the hearts they
// tap are visibly the ones that show up.
//
// Row shapes mirror `gallery_picks` / `gallery_comments` (see
// src/services/galleryProofing.ts).
// ──────────────────────────────────────────────────────────────────
const photoIdAt = (galleryId: string, index: number): string | null =>
  (demoGalleryPhotos[galleryId] ?? [])[index]?.id ?? null;

function makePick(opts: {
  id: string;
  galleryId: string;
  photoIndex: number;
  pickerIdentifier: string;
  pickerName: string;
  daysAgo: number;
  hour?: number;
}): Record<string, any> | null {
  const photoId = photoIdAt(opts.galleryId, opts.photoIndex);
  if (!photoId) return null;
  return {
    id: opts.id,
    gallery_id: opts.galleryId,
    photo_id: photoId,
    picker_identifier: opts.pickerIdentifier,
    picker_name: opts.pickerName,
    created_at: isoTimestamp(-opts.daysAgo, opts.hour ?? 20),
  };
}

export const demoGalleryPicks: Record<string, any>[] = [
  makePick({ id: 'demo-gp-1', galleryId: 'demo-g-1', photoIndex: 0, pickerIdentifier: 'demo-picker-kayla', pickerName: 'Kayla', daysAgo: 4, hour: 20 }),
  makePick({ id: 'demo-gp-2', galleryId: 'demo-g-1', photoIndex: 2, pickerIdentifier: 'demo-picker-kayla', pickerName: 'Kayla', daysAgo: 4, hour: 20 }),
  makePick({ id: 'demo-gp-3', galleryId: 'demo-g-1', photoIndex: 4, pickerIdentifier: 'demo-picker-kayla', pickerName: 'Kayla', daysAgo: 4, hour: 21 }),
  makePick({ id: 'demo-gp-4', galleryId: 'demo-g-1', photoIndex: 2, pickerIdentifier: 'demo-picker-james', pickerName: 'James', daysAgo: 3, hour: 9 }),
  makePick({ id: 'demo-gp-5', galleryId: 'demo-g-1', photoIndex: 5, pickerIdentifier: 'demo-picker-james', pickerName: 'James', daysAgo: 3, hour: 9 }),
].filter((p): p is Record<string, any> => p !== null);

function makeGalleryComment(opts: {
  id: string;
  galleryId: string;
  photoIndex: number;
  authorIdentifier: string;
  authorName: string;
  body: string;
  daysAgo: number;
  resolved?: boolean;
}): Record<string, any> | null {
  const photoId = photoIdAt(opts.galleryId, opts.photoIndex);
  if (!photoId) return null;
  return {
    id: opts.id,
    gallery_id: opts.galleryId,
    photo_id: photoId,
    author_identifier: opts.authorIdentifier,
    author_name: opts.authorName,
    body: opts.body,
    resolved: opts.resolved ?? false,
    created_at: isoTimestamp(-opts.daysAgo, 20),
  };
}

export const demoGalleryComments: Record<string, any>[] = [
  makeGalleryComment({ id: 'demo-gc-1', galleryId: 'demo-g-1', photoIndex: 0, authorIdentifier: 'demo-picker-kayla', authorName: 'Kayla', body: 'This is the one for the album cover. Can we get it as a 16x20?', daysAgo: 4 }),
  makeGalleryComment({ id: 'demo-gc-2', galleryId: 'demo-g-1', photoIndex: 2, authorIdentifier: 'demo-picker-james', authorName: 'James', body: 'Could you warm this one up slightly? The rest of the set is warmer.', daysAgo: 3 }),
  makeGalleryComment({ id: 'demo-gc-3', galleryId: 'demo-g-1', photoIndex: 4, authorIdentifier: 'demo-picker-kayla', authorName: 'Kayla', body: 'My mum cried at this one. Adding it to the print order.', daysAgo: 2, resolved: true }),
].filter((c): c is Record<string, any> => c !== null);

// ──────────────────────────────────────────────────────────────────
// DEMO-4 — project files.
//
// Replaces the 2024-era `mockProjectFiles` fallback that
// src/services/files.ts used to reach for. Same studio, same
// projects, dates derived from each project's own created_at so
// nothing reads as older than the project it belongs to.
// ──────────────────────────────────────────────────────────────────
type DemoProjectFile = Record<string, any>;

function filesForProject(projectId: string): DemoProjectFile[] {
  const project = demoProjects.find((p) => p.id === projectId);
  if (!project) return [];
  const base = project.created_at as string;
  return [
    { id: `${projectId}-file-1`, project_id: projectId, projectId, name: 'Signed agreement.pdf',       file_url: '/files/contract.pdf',  fileUrl: '/files/contract.pdf',  category: 'contract',  created_at: base, createdAt: base, uploaded_by: null },
    { id: `${projectId}-file-2`, project_id: projectId, projectId, name: 'Shot list + timeline.pdf',   file_url: '/files/shotlist.pdf',  fileUrl: '/files/shotlist.pdf',  category: 'script',    created_at: base, createdAt: base, uploaded_by: null },
    { id: `${projectId}-file-3`, project_id: projectId, projectId, name: 'Moodboard references.jpg',   file_url: demoGalleryCovers['demo-g-3'].urls.grid, fileUrl: demoGalleryCovers['demo-g-3'].urls.grid, category: 'lookbook', created_at: base, createdAt: base, uploaded_by: null },
  ];
}

export const demoProjectFiles: DemoProjectFile[] = demoProjects.flatMap((p) =>
  filesForProject(p.id as string),
);

// ──────────────────────────────────────────────────────────────────
// DEMO-4 — deliverable comments.
//
// src/services/comments.ts used to hand back one hardcoded "Love the
// colors in this version!" row for EVERY deliverable, which read as
// obviously fake the second you opened two of them. These are keyed
// to the delivery files the rest of the overlay already knows about,
// with the same client voices used in demoDeliveryVideoComments.
// ──────────────────────────────────────────────────────────────────
type DemoDeliverableComment = Record<string, any>;

function makeDeliverableComment(opts: {
  id: string;
  deliverableId: string;
  authorName: string;
  content: string;
  daysAgo: number;
  timecode?: string | null;
}): DemoDeliverableComment {
  const createdAt = isoTimestamp(-opts.daysAgo, 15);
  return {
    id: opts.id,
    deliverable_id: opts.deliverableId,
    deliverableId: opts.deliverableId,
    author_name: opts.authorName,
    userName: opts.authorName,
    userId: `demo-commenter-${opts.authorName.toLowerCase().replace(/\W+/g, '-')}`,
    content: opts.content,
    timestamp_marker: opts.timecode ?? null,
    created_at: createdAt,
    createdAt,
  };
}

export const demoDeliverableComments: DemoDeliverableComment[] = [
  makeDeliverableComment({ id: 'demo-dc-1', deliverableId: 'demo-df-7',  authorName: 'Kayla',              content: "Can we soften the audio on Dad's speech? It clips a bit.", daysAgo: 2, timecode: '1:18' }),
  makeDeliverableComment({ id: 'demo-dc-2', deliverableId: 'demo-df-7',  authorName: 'James',              content: 'Please swap in one more reception dance shot around here.', daysAgo: 1, timecode: '2:01' }),
  makeDeliverableComment({ id: 'demo-dc-3', deliverableId: 'demo-df-15', authorName: 'Amelia · Nova Coffee', content: 'Product hero looks stunning — approved.', daysAgo: 29, timecode: '0:22' }),
  makeDeliverableComment({ id: 'demo-dc-4', deliverableId: 'demo-df-1',  authorName: 'Kayla',              content: 'This is the one for the album cover.', daysAgo: 4 }),
  makeDeliverableComment({ id: 'demo-dc-5', deliverableId: 'demo-df-8',  authorName: 'Amelia · Nova Coffee', content: 'Love the crop. Can we get a 4:5 for the grid?', daysAgo: 2 }),
];

/** Deliverable comments for one deliverable, newest last. */
export function demoCommentsForDeliverable(deliverableId: string): DemoDeliverableComment[] {
  const hits = demoDeliverableComments.filter(
    (c) => c.deliverable_id === deliverableId,
  );
  if (hits.length > 0) return hits;
  // Every deliverable should look like a conversation, not a void —
  // but a generic single line for an unknown id is honest enough and
  // stays in the same voice as the seeded ones.
  return [
    makeDeliverableComment({
      id: `${deliverableId}-c-1`,
      deliverableId,
      authorName: 'Client',
      content: 'This looks great — approving from my side.',
      daysAgo: 1,
    }),
  ];
}

// ──────────────────────────────────────────────────────────────────
// DEMO-4 — creator roster (Orbit's own beta list, admin surface).
//
// Replaces `mockCreators` in src/data/mockData.ts, whose joinedDate
// values were all January 2024. Same names Orbit's admin screens
// have always shown; dates now derive from `now`.
// ──────────────────────────────────────────────────────────────────
export const demoCreators = [
  { id: 'demo-cr-1', name: 'Alex Rivera',   email: 'alex.rivera@creator.com', role: 'filmmaker',    status: 'active',   plan: 'pro',     joinedDate: isoDate(-410), foundingMember: true,  notes: 'Top performer, specialises in documentary work.' },
  { id: 'demo-cr-2', name: 'Jordan Lee',    email: 'jordan.lee@creator.com',  role: 'photographer', status: 'active',   plan: 'starter', joinedDate: isoDate(-286), foundingMember: false },
  { id: 'demo-cr-3', name: 'Taylor Morgan', email: 'taylor.m@creator.com',    role: 'editor',       status: 'invited',  plan: 'beta',    joinedDate: isoDate(-12),  foundingMember: false, notes: 'Pending onboarding call.' },
  { id: 'demo-cr-4', name: 'Casey Brooks',  email: 'casey.b@creator.com',     role: 'director',     status: 'accepted', plan: 'beta',    joinedDate: isoDate(-34),  foundingMember: false },
  { id: 'demo-cr-5', name: 'Priya Raman',   email: 'priya@ramanstudio.co',    role: 'photographer', status: 'active',   plan: 'pro',     joinedDate: isoDate(-152), foundingMember: true,  notes: 'Referred three studios in the last quarter.' },
  { id: 'demo-cr-6', name: 'Sam Okafor',    email: 'sam.okafor@creator.com',  role: 'filmmaker',    status: 'paused',   plan: 'starter', joinedDate: isoDate(-198), foundingMember: false, notes: 'On parental leave — resuming next quarter.' },
];

// ──────────────────────────────────────────────────────────────────
// Contracts — Wave 8.1a. Every ProjectWorkspace has a Contracts tab;
// before these seeds it rendered empty in Preview Studio. Row shape
// mirrors the `contracts` Supabase table (see src/services/contracts.ts).
// Statuses drawn from document_status enum: draft / sent / signed /
// paid / expired (expired is Orbit-only; ok in demo).
// ──────────────────────────────────────────────────────────────────
type DemoContract = Record<string, any>;

function makeContract(opts: {
  id: string;
  projectId: string;
  name: string;
  status: 'draft' | 'sent' | 'signed' | 'expired';
  createdOffset: number;
  signedOffset?: number;
  recipientName?: string;
  recipientEmail?: string;
}): DemoContract {
  const createdAt = isoTimestamp(opts.createdOffset, 10);
  const signedAt =
    opts.status === 'signed' && opts.signedOffset !== undefined
      ? isoTimestamp(opts.signedOffset, 12)
      : null;
  return {
    id: opts.id,
    project_id: opts.projectId,
    projectId: opts.projectId,
    name: opts.name,
    file_url: null,
    field_positions: null,
    recipient_name: opts.recipientName ?? null,
    recipient_email: opts.recipientEmail ?? null,
    signing_token: null,
    signed_pdf_url: null,
    signed_at: signedAt,
    signed_date: signedAt ? signedAt.slice(0, 10) : null,
    status: opts.status,
    created_at: createdAt,
    updated_at: signedAt ?? createdAt,
  };
}

export const demoContracts: DemoContract[] = [
  // Signed (3) — the trail behind current + past bookings.
  makeContract({ id: 'demo-ct-1', projectId: 'demo-p-11', name: 'Nova Coffee · Q1 spot production agreement', status: 'signed', createdOffset: -60, signedOffset: -55, recipientName: 'Amelia Torres', recipientEmail: 'creative@novacoffee.co' }),
  makeContract({ id: 'demo-ct-2', projectId: 'demo-p-3',  name: 'Kayla + James wedding · full-day coverage', status: 'signed', createdOffset: -50, signedOffset: -46, recipientName: 'Kayla James', recipientEmail: 'kayla.james@email.com' }),
  makeContract({ id: 'demo-ct-3', projectId: 'demo-p-1',  name: 'Sarah & Ben wedding · Aspen Lodge · full-day', status: 'signed', createdOffset: -26, signedOffset: -22, recipientName: 'Sarah Martinez', recipientEmail: 'sarah.mitchell@gmail.com' }),

  // Sent (2) — waiting on the client's signature.
  makeContract({ id: 'demo-ct-4', projectId: 'demo-p-6',  name: 'Andersson wedding · photo + film team', status: 'sent', createdOffset: -6, recipientName: 'Mira Andersson', recipientEmail: 'mira.andersson@email.com' }),
  makeContract({ id: 'demo-ct-5', projectId: 'demo-p-7',  name: 'Ember Restaurant · menu + interiors production', status: 'sent', createdOffset: -2, recipientName: 'Ember Restaurant GM', recipientEmail: 'gm@emberrestaurant.com' }),

  // Draft (2) — studio is still authoring.
  makeContract({ id: 'demo-ct-6', projectId: 'demo-p-9',  name: 'Willow Boutique · brand refresh · scope + terms', status: 'draft', createdOffset: -1 }),
  makeContract({ id: 'demo-ct-7', projectId: 'demo-p-8',  name: 'Chen · editorial · usage + release', status: 'draft', createdOffset: 0 }),

  // Expired (1) — the pitch never converted.
  makeContract({ id: 'demo-ct-8', projectId: 'demo-p-12', name: 'Dr. Reyes · headshot retainer · one-off engagement', status: 'expired', createdOffset: -120, recipientName: 'Dr. Alicia Reyes' }),
];

// ──────────────────────────────────────────────────────────────────
// Estimates — quick-quote line items sent before the full contract.
// Row shape mirrors `estimates` table (amount + status). Statuses
// use the same document_status enum as contracts.
// ──────────────────────────────────────────────────────────────────
type DemoEstimate = Record<string, any>;

function makeEstimate(opts: {
  id: string;
  projectId: string;
  amount: number;
  status: 'draft' | 'sent' | 'viewed' | 'signed';
  createdOffset: number;
  validUntilOffset?: number;
}): DemoEstimate {
  const createdAt = isoTimestamp(opts.createdOffset, 10);
  return {
    id: opts.id,
    project_id: opts.projectId,
    projectId: opts.projectId,
    amount: opts.amount,
    file_url: null,
    status: opts.status,
    valid_until: isoDate(opts.validUntilOffset ?? opts.createdOffset + 30),
    created_at: createdAt,
    updated_at: createdAt,
  };
}

export const demoEstimates: DemoEstimate[] = [
  makeEstimate({ id: 'demo-es-1', projectId: 'demo-p-9',  amount: 4400, status: 'draft',  createdOffset: -1 }),
  makeEstimate({ id: 'demo-es-2', projectId: 'demo-p-10', amount: 1400, status: 'sent',   createdOffset: -3, validUntilOffset: 21 }),
  makeEstimate({ id: 'demo-es-3', projectId: 'demo-p-7',  amount: 9200, status: 'viewed', createdOffset: -8, validUntilOffset: 22 }),
  makeEstimate({ id: 'demo-es-4', projectId: 'demo-p-6',  amount: 7400, status: 'signed', createdOffset: -30 }),
  makeEstimate({ id: 'demo-es-5', projectId: 'demo-p-8',  amount: 3800, status: 'sent',   createdOffset: -6, validUntilOffset: 24 }),
];

// ──────────────────────────────────────────────────────────────────
// Documents — Document Builder rows (proposals / contracts / invoices
// unified as OrbitDocument, see src/services/documents.ts). Mix of
// statuses + project types so the docs tab feels lived-in.
// ──────────────────────────────────────────────────────────────────
type DemoDocument = Record<string, any>;

function makeDocument(opts: {
  id: string;
  projectId: string | null;
  clientId: string | null;
  title: string;
  status: 'draft' | 'sent' | 'viewed' | 'signed' | 'paid' | 'expired' | 'void';
  paymentStatus?: 'unpaid' | 'pending' | 'paid' | 'refunded';
  totalCents: number;
  projectType?: string | null;
  createdOffset: number;
  sentOffset?: number;
  viewedOffset?: number;
  signedOffset?: number;
  paidOffset?: number;
  signers?: Array<{ role: string; name: string; email: string | null; signed_at: string | null }>;
}): DemoDocument {
  const createdAt = isoTimestamp(opts.createdOffset, 10);
  const sentAt      = opts.sentOffset   !== undefined ? isoTimestamp(opts.sentOffset,   11) : null;
  const viewedAt    = opts.viewedOffset !== undefined ? isoTimestamp(opts.viewedOffset, 12) : null;
  const signedAt    = opts.signedOffset !== undefined ? isoTimestamp(opts.signedOffset, 13) : null;
  const paidAt      = opts.paidOffset   !== undefined ? isoTimestamp(opts.paidOffset,   14) : null;
  return {
    id: opts.id,
    org_id: 'demo-org',
    project_id: opts.projectId,
    client_id: opts.clientId,
    title: opts.title,
    status: opts.status,
    kind: 'document',
    payment_status: opts.paymentStatus ?? 'unpaid',
    public_token: `demo-token-${opts.id}`,
    is_template: false,
    project_type: opts.projectType ?? null,
    total_cents: opts.totalCents,
    sent_at: sentAt,
    first_viewed_at: viewedAt,
    signed_at: signedAt,
    paid_at: paidAt,
    expires_at: null,
    signers: opts.signers ?? [
      { role: 'primary', name: 'Primary signer', email: null, signed_at: signedAt },
    ],
    created_by: 'demo-studio-user',
    created_at: createdAt,
    updated_at: paidAt ?? signedAt ?? viewedAt ?? sentAt ?? createdAt,
  };
}

export const demoDocuments: DemoDocument[] = [
  // Fully signed + paid — the Q1 spot the studio just closed out.
  makeDocument({ id: 'demo-doc-1', projectId: 'demo-p-11', clientId: 'demo-c-3', title: 'Nova Coffee · Q1 spot · proposal + contract', status: 'signed', paymentStatus: 'paid', totalCents: 320000, projectType: 'brand', createdOffset: -62, sentOffset: -58, viewedOffset: -57, signedOffset: -55, paidOffset: -50 }),

  // Signed, waiting on final payment.
  makeDocument({ id: 'demo-doc-2', projectId: 'demo-p-3',  clientId: 'demo-c-8', title: 'Kayla + James · wedding contract + deposit invoice', status: 'signed', paymentStatus: 'paid', totalCents: 620000, projectType: 'wedding', createdOffset: -50, sentOffset: -48, viewedOffset: -47, signedOffset: -46, paidOffset: -30 }),
  makeDocument({ id: 'demo-doc-3', projectId: 'demo-p-1',  clientId: 'demo-c-1', title: 'Sarah & Ben · wedding contract · Aspen Lodge', status: 'signed', paymentStatus: 'pending', totalCents: 480000, projectType: 'wedding', createdOffset: -26, sentOffset: -24, viewedOffset: -23, signedOffset: -22 }),

  // Viewed but not signed — the follow-up nudge lives here.
  makeDocument({ id: 'demo-doc-4', projectId: 'demo-p-6',  clientId: 'demo-c-11', title: 'Andersson · Brooklyn loft wedding · proposal', status: 'viewed', totalCents: 740000, projectType: 'wedding', createdOffset: -6, sentOffset: -5, viewedOffset: -3 }),

  // Sent — awaiting a first open.
  makeDocument({ id: 'demo-doc-5', projectId: 'demo-p-7',  clientId: 'demo-c-10', title: 'Ember Restaurant · menu + interiors scope + terms', status: 'sent', totalCents: 920000, projectType: 'commercial', createdOffset: -2, sentOffset: -1 }),
  makeDocument({ id: 'demo-doc-6', projectId: 'demo-p-4',  clientId: 'demo-c-3', title: 'Nova Coffee · Q2 balance invoice', status: 'sent', totalCents: 110000, projectType: 'brand', createdOffset: -2, sentOffset: -2 }),

  // Draft — studio is authoring.
  makeDocument({ id: 'demo-doc-7', projectId: 'demo-p-9',  clientId: 'demo-c-6', title: 'Willow Boutique · brand refresh · scope of work', status: 'draft', totalCents: 440000, projectType: 'brand', createdOffset: -1 }),
  makeDocument({ id: 'demo-doc-8', projectId: 'demo-p-8',  clientId: 'demo-c-5', title: 'Chen · editorial · usage + model release', status: 'draft', totalCents: 380000, projectType: 'editorial', createdOffset: 0 }),
  makeDocument({ id: 'demo-doc-9', projectId: 'demo-p-1',  clientId: 'demo-c-1', title: 'Sarah & Ben · pre-shoot questionnaire', status: 'draft', totalCents: 0, projectType: 'wedding', createdOffset: -3 }),

  // Expired — a stale proposal that never converted.
  makeDocument({ id: 'demo-doc-10', projectId: 'demo-p-12', clientId: 'demo-c-7', title: 'Dr. Reyes · headshot proposal (v1)', status: 'expired', totalCents: 240000, projectType: 'portrait', createdOffset: -140, sentOffset: -135, viewedOffset: -132 }),
];

// ──────────────────────────────────────────────────────────────────
// Tasks — per-project checklist items. Row shape mirrors the
// `tasks` Supabase table. Status enum: todo / in_progress / review /
// completed. Priority: low / medium / high / urgent.
// Every active project gets a mix of done, in-flight, and open tasks
// with a spread of due dates (overdue, this week, next week, none).
// ──────────────────────────────────────────────────────────────────
type DemoTask = Record<string, any>;

function makeTask(opts: {
  id: string;
  projectId: string;
  title: string;
  description?: string | null;
  status: 'todo' | 'in_progress' | 'review' | 'completed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  dueOffset?: number | null;
  createdOffset: number;
}): DemoTask {
  const createdAt = isoTimestamp(opts.createdOffset, 9);
  return {
    id: opts.id,
    project_id: opts.projectId,
    projectId: opts.projectId,
    title: opts.title,
    description: opts.description ?? null,
    status: opts.status,
    priority: opts.priority,
    due_date: opts.dueOffset === null || opts.dueOffset === undefined ? null : isoDate(opts.dueOffset),
    assigned_to: null,
    created_at: createdAt,
    updated_at: createdAt,
  };
}

export const demoTasks: DemoTask[] = [
  // ── Sarah & Ben wedding (p-1, TODAY shoot) — production heat ──
  makeTask({ id: 'demo-tk-1',  projectId: 'demo-p-1', title: 'Confirm shot list with couple', status: 'completed', priority: 'high',   dueOffset: -3, createdOffset: -10 }),
  makeTask({ id: 'demo-tk-2',  projectId: 'demo-p-1', title: 'Charge every battery + format all cards', status: 'completed', priority: 'urgent', dueOffset: -1, createdOffset: -2 }),
  makeTask({ id: 'demo-tk-3',  projectId: 'demo-p-1', title: 'Print family formal list · pass to second shooter', status: 'in_progress', priority: 'high', dueOffset: 0, createdOffset: -2 }),
  makeTask({ id: 'demo-tk-4',  projectId: 'demo-p-1', title: 'Cull day-one selects · upload preview gallery', status: 'todo', priority: 'high', dueOffset: 2, createdOffset: -1 }),
  makeTask({ id: 'demo-tk-5',  projectId: 'demo-p-1', title: 'Send thank-you gift to the couple', status: 'todo', priority: 'medium', dueOffset: 5, createdOffset: -1 }),
  makeTask({ id: 'demo-tk-6',  projectId: 'demo-p-1', title: 'Draft blog post for social preview', status: 'todo', priority: 'low', dueOffset: null, createdOffset: 0 }),

  // ── Marcus & Elle engagement (p-2, TODAY) ──
  makeTask({ id: 'demo-tk-7',  projectId: 'demo-p-2', title: 'Scout downtown rooftop for lighting', status: 'completed', priority: 'high', dueOffset: -2, createdOffset: -5 }),
  makeTask({ id: 'demo-tk-8',  projectId: 'demo-p-2', title: 'Text location pin at 5:30pm', status: 'in_progress', priority: 'urgent', dueOffset: 0, createdOffset: -1 }),
  makeTask({ id: 'demo-tk-9',  projectId: 'demo-p-2', title: 'Cull + preview gallery within 48h', status: 'todo', priority: 'high', dueOffset: 2, createdOffset: 0 }),
  makeTask({ id: 'demo-tk-10', projectId: 'demo-p-2', title: 'Send print recommendations', status: 'todo', priority: 'medium', dueOffset: 7, createdOffset: 0 }),

  // ── Kayla + James wedding edit (p-3) ──
  makeTask({ id: 'demo-tk-11', projectId: 'demo-p-3', title: 'V1 color pass on ceremony', status: 'completed', priority: 'high',   dueOffset: -6, createdOffset: -10 }),
  makeTask({ id: 'demo-tk-12', projectId: 'demo-p-3', title: 'Cut highlight film to 3:45', status: 'in_progress', priority: 'urgent', dueOffset: 1, createdOffset: -3 }),
  makeTask({ id: 'demo-tk-13', projectId: 'demo-p-3', title: 'Address audio note on Dad speech', status: 'in_progress', priority: 'high', dueOffset: 2, createdOffset: -1 }),
  makeTask({ id: 'demo-tk-14', projectId: 'demo-p-3', title: 'Package delivery link · send balance invoice', status: 'todo', priority: 'high', dueOffset: 3, createdOffset: -1 }),
  makeTask({ id: 'demo-tk-15', projectId: 'demo-p-3', title: 'Archive project raws to cold storage', status: 'todo', priority: 'low', dueOffset: 14, createdOffset: 0 }),

  // ── Nova Coffee Q2 brand shoot (p-4, review stage) ──
  makeTask({ id: 'demo-tk-16', projectId: 'demo-p-4', title: 'Deliver hero + product selects', status: 'completed', priority: 'high', dueOffset: -4, createdOffset: -8 }),
  makeTask({ id: 'demo-tk-17', projectId: 'demo-p-4', title: 'Address Amelia\'s hex-color note on hero', status: 'in_progress', priority: 'high', dueOffset: 1, createdOffset: -2 }),
  makeTask({ id: 'demo-tk-18', projectId: 'demo-p-4', title: 'Send balance invoice', status: 'todo', priority: 'urgent', dueOffset: -1, createdOffset: -3 }), // overdue
  makeTask({ id: 'demo-tk-19', projectId: 'demo-p-4', title: 'Add Q2 stills to portfolio site', status: 'todo', priority: 'low', dueOffset: null, createdOffset: -1 }),
  makeTask({ id: 'demo-tk-20', projectId: 'demo-p-4', title: 'Get Amelia\'s testimonial for landing page', status: 'todo', priority: 'medium', dueOffset: 10, createdOffset: 0 }),

  // ── Hollins family session (p-5, editing) ──
  makeTask({ id: 'demo-tk-21', projectId: 'demo-p-5', title: 'Cull to 60 selects', status: 'completed', priority: 'medium', dueOffset: -1, createdOffset: -3 }),
  makeTask({ id: 'demo-tk-22', projectId: 'demo-p-5', title: 'Color grade + skin tone pass', status: 'in_progress', priority: 'high', dueOffset: 2, createdOffset: -1 }),
  makeTask({ id: 'demo-tk-23', projectId: 'demo-p-5', title: 'Publish gallery + email link', status: 'todo', priority: 'high', dueOffset: 4, createdOffset: 0 }),
  makeTask({ id: 'demo-tk-24', projectId: 'demo-p-5', title: 'Suggest print + album packages', status: 'todo', priority: 'medium', dueOffset: 8, createdOffset: 0 }),

  // ── Andersson wedding (p-6, pre_production) ──
  makeTask({ id: 'demo-tk-25', projectId: 'demo-p-6', title: 'Send contract + deposit invoice', status: 'completed', priority: 'urgent', dueOffset: -5, createdOffset: -8 }),
  makeTask({ id: 'demo-tk-26', projectId: 'demo-p-6', title: 'Book venue walkthrough', status: 'in_progress', priority: 'high', dueOffset: 3, createdOffset: -2 }),
  makeTask({ id: 'demo-tk-27', projectId: 'demo-p-6', title: 'Second-shooter contract', status: 'todo', priority: 'high', dueOffset: 14, createdOffset: -1 }),
  makeTask({ id: 'demo-tk-28', projectId: 'demo-p-6', title: 'Timeline + shot list draft', status: 'todo', priority: 'medium', dueOffset: 21, createdOffset: 0 }),

  // ── Ember Restaurant commercial (p-7, pre_production) ──
  makeTask({ id: 'demo-tk-29', projectId: 'demo-p-7', title: 'Menu tasting + pre-pro with chef', status: 'in_progress', priority: 'high', dueOffset: 4, createdOffset: -3 }),
  makeTask({ id: 'demo-tk-30', projectId: 'demo-p-7', title: 'Lock crew list for shoot days', status: 'todo', priority: 'high', dueOffset: 7, createdOffset: -1 }),
  makeTask({ id: 'demo-tk-31', projectId: 'demo-p-7', title: 'Confirm equipment rental hold', status: 'todo', priority: 'medium', dueOffset: 12, createdOffset: 0 }),
  makeTask({ id: 'demo-tk-32', projectId: 'demo-p-7', title: 'Shot plan v1 for GM review', status: 'todo', priority: 'high', dueOffset: 10, createdOffset: 0 }),

  // ── Chen editorial (p-8, production) ──
  makeTask({ id: 'demo-tk-33', projectId: 'demo-p-8', title: 'Model releases signed', status: 'completed', priority: 'urgent', dueOffset: -2, createdOffset: -6 }),
  makeTask({ id: 'demo-tk-34', projectId: 'demo-p-8', title: 'Test shoot lighting setups', status: 'in_progress', priority: 'high', dueOffset: 3, createdOffset: -2 }),
  makeTask({ id: 'demo-tk-35', projectId: 'demo-p-8', title: 'Confirm wardrobe stylist', status: 'todo', priority: 'high', dueOffset: 4, createdOffset: -1 }),
  makeTask({ id: 'demo-tk-36', projectId: 'demo-p-8', title: 'Send test frames to editor', status: 'todo', priority: 'medium', dueOffset: 6, createdOffset: 0 }),

  // ── Willow Boutique brand refresh (p-9, discovery) ──
  makeTask({ id: 'demo-tk-37', projectId: 'demo-p-9', title: 'Send scope of work + estimate', status: 'in_progress', priority: 'high', dueOffset: 2, createdOffset: -1 }),
  makeTask({ id: 'demo-tk-38', projectId: 'demo-p-9', title: 'Moodboard round 1', status: 'todo', priority: 'medium', dueOffset: 9, createdOffset: 0 }),
  makeTask({ id: 'demo-tk-39', projectId: 'demo-p-9', title: 'Book scout day', status: 'todo', priority: 'medium', dueOffset: 12, createdOffset: 0 }),

  // ── Sunset Kids family session (p-10, discovery) ──
  makeTask({ id: 'demo-tk-40', projectId: 'demo-p-10', title: 'Send estimate + sample gallery', status: 'in_progress', priority: 'high', dueOffset: 1, createdOffset: -1 }),
  makeTask({ id: 'demo-tk-41', projectId: 'demo-p-10', title: 'Confirm location + timing', status: 'todo', priority: 'medium', dueOffset: 6, createdOffset: 0 }),
  makeTask({ id: 'demo-tk-42', projectId: 'demo-p-10', title: 'Wardrobe recommendations doc', status: 'todo', priority: 'low', dueOffset: 8, createdOffset: 0 }),
];

// ──────────────────────────────────────────────────────────────────
// Milestones — flat list keyed by project. Every project already
// has an embedded milestones array on `demoProjects[i].milestones`,
// but useMilestones() queries a flat table shape. This is that shape
// (Row = milestones table), flattened per project.
// ──────────────────────────────────────────────────────────────────
type DemoMilestone = Record<string, any>;

/** Six-stage template shared by every non-delivery project. */
const MILESTONE_TEMPLATE: Array<{ name: string; dueOffset: number }> = [
  { name: 'Discovery',      dueOffset: -30 },
  { name: 'Pre-production', dueOffset: -14 },
  { name: 'Production',     dueOffset: 0   },
  { name: 'Editing',        dueOffset: 10  },
  { name: 'Review',         dueOffset: 20  },
  { name: 'Delivery',       dueOffset: 28  },
];

function buildMilestonesForProject(project: DemoProject): DemoMilestone[] {
  const doneMilestones = Array.isArray(project.milestones)
    ? project.milestones.filter((m: any) => m.completed).length
    : 0;
  // Anchor the due dates around the project's shoot / due date so
  // completed milestones sit in the past and open ones sit ahead.
  const shootDayOffset =
    project.scheduled_date
      ? Math.round(
          (new Date(project.scheduled_date).getTime() - now.getTime()) / dayMs,
        )
      : 0;
  return MILESTONE_TEMPLATE.map((stage, idx) => {
    const dueOffset = shootDayOffset + stage.dueOffset;
    const completed = idx < doneMilestones;
    return {
      id: `${project.id}-ms-${idx + 1}`,
      project_id: project.id,
      projectId: project.id,
      name: stage.name,
      completed,
      due_date: isoDate(dueOffset),
      sort_order: idx + 1,
      created_at: isoTimestamp(project.createdAt ? -30 : -30, 8),
    };
  });
}

export const demoMilestones: DemoMilestone[] = demoProjects.flatMap((p) =>
  buildMilestonesForProject(p),
);

// ──────────────────────────────────────────────────────────────────
// Shots — per-project shot lists for shoot-based projects. Uses the
// `shots` table shape. `type` is constrained to the shot_type enum
// (WS, MS, CU, ECU, Drone, Detail, POV, Tracking, Steadicam,
// Handheld, Timelapse, Slow-Mo).
// ──────────────────────────────────────────────────────────────────
type DemoShot = Record<string, any>;
type ShotType =
  | 'WS' | 'MS' | 'CU' | 'ECU' | 'Drone' | 'Detail'
  | 'POV' | 'Tracking' | 'Steadicam' | 'Handheld' | 'Timelapse' | 'Slow-Mo';

function makeShot(opts: {
  id: string;
  projectId: string;
  scene: string;
  type: ShotType;
  location: string;
  notes?: string | null;
  needsStoryboard?: boolean;
  sortOrder: number;
  daysAgo?: number;
}): DemoShot {
  const created = isoTimestamp(-(opts.daysAgo ?? 10), 8);
  return {
    id: opts.id,
    project_id: opts.projectId,
    projectId: opts.projectId,
    scene: opts.scene,
    type: opts.type,
    location: opts.location,
    notes: opts.notes ?? null,
    reference_url: null,
    needs_storyboard: opts.needsStoryboard ?? false,
    sort_order: opts.sortOrder,
    created_at: created,
    deleted_at: null,
  };
}

/**
 * Build a shot list from a compact spec. Keeps the exported array
 * readable while still generating 15-20 shots per shoot project.
 */
function buildShots(
  projectId: string,
  scenes: Array<{ scene: string; type: ShotType; location: string; note?: string; storyboard?: boolean }>,
): DemoShot[] {
  return scenes.map((s, i) =>
    makeShot({
      id: `${projectId}-shot-${i + 1}`,
      projectId,
      scene: s.scene,
      type: s.type,
      location: s.location,
      notes: s.note ?? null,
      needsStoryboard: s.storyboard ?? false,
      sortOrder: i + 1,
      daysAgo: 5,
    }),
  );
}

export const demoShots: DemoShot[] = [
  // ── Sarah & Ben wedding (p-1) — ceremony/portraits/details/reception ──
  ...buildShots('demo-p-1', [
    { scene: 'Getting ready',  type: 'CU',     location: 'Bridal suite',  note: 'Dress, rings, letter reading' },
    { scene: 'Getting ready',  type: 'Detail', location: 'Bridal suite',  note: 'Rings on lace' },
    { scene: 'First look',     type: 'MS',     location: 'Lodge garden',  storyboard: true },
    { scene: 'Ceremony',       type: 'WS',     location: 'Aspen Lodge',   note: 'Full altar reveal' },
    { scene: 'Ceremony',       type: 'MS',     location: 'Aspen Lodge',   note: 'Vows exchange' },
    { scene: 'Ceremony',       type: 'CU',     location: 'Aspen Lodge',   note: 'First kiss' },
    { scene: 'Ceremony',       type: 'CU',     location: 'Aspen Lodge',   note: 'Ring exchange · father hands over' },
    { scene: 'Portraits',      type: 'MS',     location: 'Meadow path',   storyboard: true },
    { scene: 'Portraits',      type: 'CU',     location: 'Meadow path',   note: 'Couple laughing' },
    { scene: 'Family formals', type: 'WS',     location: 'Lodge steps',   note: 'Full family group' },
    { scene: 'Family formals', type: 'MS',     location: 'Lodge steps',   note: 'Immediate family only' },
    { scene: 'Reception',      type: 'WS',     location: 'Reception tent', note: 'Room reveal from entrance' },
    { scene: 'Reception',      type: 'MS',     location: 'Reception tent', note: 'First dance' },
    { scene: 'Reception',      type: 'CU',     location: 'Reception tent', note: 'Toast tears · dad speech' },
    { scene: 'Reception',      type: 'Detail', location: 'Reception tent', note: 'Cake · florals · signage' },
    { scene: 'Reception',      type: 'Drone',  location: 'Lodge exterior', note: 'Send-off sparklers' },
  ]),

  // ── Marcus & Elle engagement (p-2) ──
  ...buildShots('demo-p-2', [
    { scene: 'Arrival',         type: 'MS', location: 'Rooftop entrance', note: 'Walk-up establishing' },
    { scene: 'Golden portraits', type: 'MS', location: 'Rooftop west side' },
    { scene: 'Golden portraits', type: 'CU', location: 'Rooftop west side', note: 'Close on eyes + smile' },
    { scene: 'Golden portraits', type: 'WS', location: 'Rooftop west side', note: 'Skyline reveal' },
    { scene: 'Candids',         type: 'MS', location: 'Rooftop bar area', note: 'Laughter · walking' },
    { scene: 'Candids',         type: 'CU', location: 'Rooftop bar area', note: 'Hand holding · ring detail' },
    { scene: 'Details',         type: 'Detail', location: 'Rooftop', note: 'Ring + outfit textures' },
    { scene: 'Sunset',          type: 'WS', location: 'Rooftop east side', note: 'Silhouette against sky', storyboard: true },
    { scene: 'Sunset',          type: 'CU', location: 'Rooftop east side', note: 'Foreheads together' },
    { scene: 'Blue hour',       type: 'MS', location: 'Rooftop lights', note: 'City lights bokeh' },
  ]),

  // ── Kayla + James wedding (p-3) — full-day set ──
  ...buildShots('demo-p-3', [
    { scene: 'Prep',            type: 'CU', location: 'Bride suite' },
    { scene: 'Prep',            type: 'MS', location: 'Groom suite' },
    { scene: 'First look',      type: 'WS', location: 'Garden path', storyboard: true },
    { scene: 'Ceremony',        type: 'WS', location: 'Vineyard altar', note: 'Establishing' },
    { scene: 'Ceremony',        type: 'MS', location: 'Vineyard altar', note: 'Vows' },
    { scene: 'Ceremony',        type: 'CU', location: 'Vineyard altar', note: 'First kiss' },
    { scene: 'Cocktail hour',   type: 'MS', location: 'Barn patio' },
    { scene: 'Cocktail hour',   type: 'Detail', location: 'Barn patio', note: 'Signature cocktails' },
    { scene: 'Portraits',       type: 'MS', location: 'Vineyard rows' },
    { scene: 'Portraits',       type: 'CU', location: 'Vineyard rows' },
    { scene: 'Reception',       type: 'WS', location: 'Barn interior', note: 'Room reveal' },
    { scene: 'Reception',       type: 'MS', location: 'Barn interior', note: 'First dance' },
    { scene: 'Reception',       type: 'CU', location: 'Barn interior', note: 'Toasts' },
    { scene: 'Reception',       type: 'Detail', location: 'Barn interior', note: 'Cake · signage · florals' },
    { scene: 'Send-off',        type: 'Drone', location: 'Vineyard aerial', note: 'Sparklers · car depart' },
  ]),

  // ── Nova Coffee Q2 brand shoot (p-4) ──
  ...buildShots('demo-p-4', [
    { scene: 'Hero',      type: 'CU',     location: 'Studio A', note: 'Product on gradient · brand color' },
    { scene: 'Hero',      type: 'Detail', location: 'Studio A', note: 'Texture close-ups' },
    { scene: 'Lifestyle', type: 'MS',     location: 'Studio B', note: 'Model applying product' },
    { scene: 'Lifestyle', type: 'CU',     location: 'Studio B', note: 'Hands · packaging' },
    { scene: 'On-location', type: 'WS',   location: 'Rooftop', note: 'Natural light · morning' },
    { scene: 'On-location', type: 'MS',   location: 'Rooftop' },
    { scene: 'Product',   type: 'Detail', location: 'Studio A', note: 'Multi-SKU flatlay' },
    { scene: 'Product',   type: 'Detail', location: 'Studio A', note: 'Individual SKU · rotating' },
    { scene: 'BTS',       type: 'Handheld', location: 'Studio', note: 'For sizzle reel' },
    { scene: 'BTS',       type: 'Handheld', location: 'Rooftop' },
    { scene: 'Testimonial', type: 'MS',   location: 'Studio B', note: 'Founder to camera' },
  ]),

  // ── Hollins family (p-5) ──
  ...buildShots('demo-p-5', [
    { scene: 'Arrival',    type: 'WS', location: 'Front porch' },
    { scene: 'Family group', type: 'WS', location: 'Living room', note: 'Whole family portrait' },
    { scene: 'Family group', type: 'MS', location: 'Living room', note: 'Parents + kids' },
    { scene: 'Newborn',    type: 'CU', location: 'Nursery', note: 'On blanket' },
    { scene: 'Newborn',    type: 'Detail', location: 'Nursery', note: 'Feet, hands, eyelashes' },
    { scene: 'Toddler',    type: 'MS', location: 'Playroom', note: 'Playing with blocks' },
    { scene: 'Sibling',    type: 'CU', location: 'Nursery', note: 'Toddler meeting baby' },
    { scene: 'Candids',    type: 'MS', location: 'Kitchen', note: 'Cooking · morning light' },
    { scene: 'Candids',    type: 'CU', location: 'Kitchen', note: 'Coffee cups · little hands' },
    { scene: 'Backyard',   type: 'WS', location: 'Backyard' },
    { scene: 'Backyard',   type: 'MS', location: 'Backyard', note: 'Chasing bubbles' },
  ]),

  // ── Andersson wedding (p-6) — Brooklyn loft ──
  ...buildShots('demo-p-6', [
    { scene: 'Prep',       type: 'MS', location: 'Airbnb loft' },
    { scene: 'Prep',       type: 'Detail', location: 'Airbnb loft', note: 'Dress hanging · shoes · florals' },
    { scene: 'Ceremony',   type: 'WS', location: 'Loft main floor' },
    { scene: 'Ceremony',   type: 'MS', location: 'Loft main floor', note: 'Vows' },
    { scene: 'Ceremony',   type: 'CU', location: 'Loft main floor', note: 'First kiss' },
    { scene: 'Portraits',  type: 'WS', location: 'Rooftop', note: 'Skyline behind' },
    { scene: 'Portraits',  type: 'MS', location: 'Rooftop' },
    { scene: 'Reception',  type: 'WS', location: 'Loft', note: 'Reception setup reveal' },
    { scene: 'Reception',  type: 'MS', location: 'Loft', note: 'First dance' },
    { scene: 'Reception',  type: 'CU', location: 'Loft', note: 'Toasts' },
    { scene: 'Reception',  type: 'Detail', location: 'Loft', note: 'Table settings · florals' },
    { scene: 'Send-off',   type: 'MS', location: 'Loft entrance', note: 'Sparklers' },
  ]),

  // ── Ember Restaurant — menu + interiors (p-7) ──
  ...buildShots('demo-p-7', [
    { scene: 'Establishing', type: 'Drone', location: 'Ember exterior', note: 'Aerial establishing · dusk signage' },
    { scene: 'Menu hero',    type: 'CU', location: 'Kitchen pass', note: 'Tasting-menu hero plate' },
    { scene: 'Menu hero',    type: 'Slow-Mo', location: 'Kitchen pass', note: 'Slow-mo sauce pour' },
    { scene: 'Dining room',  type: 'WS', location: 'Main floor', note: 'Room reveal · golden lamps' },
    { scene: 'Dining room',  type: 'MS', location: 'Booth row', note: 'Guests mid-service' },
    { scene: 'Kitchen',      type: 'Tracking', location: 'Line', note: 'Following the expo run' },
    { scene: 'Kitchen',      type: 'Handheld', location: 'Line', note: 'Flame + plating energy' },
    { scene: 'Bar',          type: 'Steadicam', location: 'Bar', note: 'Push-in on signature pour' },
    { scene: 'Timelapse',    type: 'Timelapse', location: 'Main floor', note: 'Empty → full house' },
    { scene: 'Details',      type: 'Detail', location: 'Tables', note: 'Ceramics · linen · menu print' },
  ]),

  // ── Chen editorial (p-8) ──
  ...buildShots('demo-p-8', [
    { scene: 'Fashion',   type: 'WS', location: 'Studio backdrop', note: 'Full-length editorial' },
    { scene: 'Fashion',   type: 'MS', location: 'Studio backdrop' },
    { scene: 'Fashion',   type: 'CU', location: 'Studio backdrop', note: 'Beauty portrait' },
    { scene: 'Fashion',   type: 'Detail', location: 'Studio backdrop', note: 'Garment textures' },
    { scene: 'Beauty',    type: 'CU', location: 'Studio B', note: 'Makeup close-up' },
    { scene: 'Beauty',    type: 'ECU', location: 'Studio B', note: 'Eyes · lashes' },
    { scene: 'On-location', type: 'MS', location: 'Rooftop', note: 'Concrete backdrop' },
    { scene: 'On-location', type: 'WS', location: 'Alley', note: 'Editorial narrative frame' },
    { scene: 'Narrative', type: 'MS', location: 'Alley', note: 'Story sequence 1/3' },
    { scene: 'Narrative', type: 'MS', location: 'Alley', note: 'Story sequence 2/3' },
    { scene: 'Narrative', type: 'CU', location: 'Alley', note: 'Story sequence 3/3', storyboard: true },
  ]),

  // ── Willow Boutique brand refresh (p-9, discovery — early scouting) ──
  ...buildShots('demo-p-9', [
    { scene: 'Scout',   type: 'WS', location: 'Warehouse space', note: 'Wide reference of walls / light' },
    { scene: 'Scout',   type: 'MS', location: 'Warehouse space' },
    { scene: 'Scout',   type: 'Detail', location: 'Warehouse space', note: 'Texture references' },
    { scene: 'Scout',   type: 'WS', location: 'Rooftop option', note: 'Backup exterior' },
    { scene: 'Scout',   type: 'MS', location: 'Rooftop option' },
    { scene: 'Reference', type: 'CU', location: 'Studio backdrop', note: 'Product angle test' },
    { scene: 'Reference', type: 'Detail', location: 'Studio backdrop' },
  ]),

  // ── Sunset Kids family (p-10) ──
  ...buildShots('demo-p-10', [
    { scene: 'Portraits', type: 'MS', location: 'Piedmont Park path', note: 'Whole family walking' },
    { scene: 'Portraits', type: 'CU', location: 'Piedmont Park path', note: 'Each kid solo' },
    { scene: 'Candids',   type: 'MS', location: 'Piedmont Park path', note: 'Piggyback race' },
    { scene: 'Candids',   type: 'MS', location: 'Piedmont Park lawn', note: 'Picnic blanket · snacks' },
    { scene: 'Sunset',    type: 'WS', location: 'Park hilltop', note: 'Skyline behind', storyboard: true },
    { scene: 'Sunset',    type: 'CU', location: 'Park hilltop', note: 'Parents + kids huddle' },
    { scene: 'Details',   type: 'Detail', location: 'Park bench', note: 'Tiny shoes · held hands' },
    { scene: 'Blue hour', type: 'MS', location: 'Park fountain', note: 'Lit fountain background' },
  ]),
];

// ──────────────────────────────────────────────────────────────────
// Stats — precomputed metrics the "Today at a glance" strip shows
// (Inquiries today, Projects active, Contracts sent, Payments received).
// MiniAnalyticsBar reads these directly when isDemo is on.
// ──────────────────────────────────────────────────────────────────
export const demoStats = {
  inquiriesToday: demoInquiries.filter(
    (i) => Math.abs(new Date(i.submitted_at).getTime() - now.getTime()) < dayMs,
  ).length,
  projectsActive: demoProjects.filter(
    (p) => !['delivered', 'archived', 'cancelled', 'completed'].includes(p.status),
  ).length,
  contractsSent: 7,
  contractsSigned: 5,
  paymentsReceivedCents: 2480000, // $24,800 — feels like a working studio month
  paymentsReceivedFormatted: '$24,800',
  monthlyRecurring: 7400,
};

// ──────────────────────────────────────────────────────────────────
// Wave 8.2 — Nova surfaces
// ──────────────────────────────────────────────────────────────────
//
// Credits, suggestion cards, past conversations, and a pre-formatted
// chat-context string that Preview Studio uses to power Nova during
// the keynote. Kept alongside the rest of the overlay so the whole
// "example universe" lives in one file.
//
// Consumers:
//   • useNovaSuggestions — short-circuits to `demoNovaSuggestions`
//     when isDemo.
//   • DemoStoreContext — hydrates a `novaConversations` entity from
//     `demoNovaConversations` so the history sheet is populated.
//   • Assistant.tsx — appends `buildDemoChatContext()` to the
//     outgoing POST body when isDemo so live Nova replies reference
//     the demo roster coherently instead of "you have no data yet."

export const demoNovaCredits = {
  credits: 1247,
  renewalInDays: 18,
  planLabel: 'Studio',
  monthlyBaseline: 2000,
};

export const demoNovaCreditTransactions = [
  {
    id: 'demo-nct-1',
    delta: -28,
    label: 'Image generation · Ember dinner shoot',
    occurred_at: isoTimestamp(-1, 14),
  },
  {
    id: 'demo-nct-2',
    delta: -12,
    label: 'Draft delivery email · Marcus + Elle',
    occurred_at: isoTimestamp(-2, 10),
  },
  {
    id: 'demo-nct-3',
    delta: -6,
    label: "Chat · what's on my plate this week",
    occurred_at: isoTimestamp(-3, 9),
  },
  {
    id: 'demo-nct-4',
    delta: 2000,
    label: 'Studio plan · monthly renewal',
    occurred_at: isoTimestamp(-12, 4),
  },
];

/**
 * Six Nova suggestion cards that reference the actual demo roster.
 * Field shape mirrors `NovaSuggestion` from `/types/novaSuggestion.ts`
 * so `useNovaSuggestions` can drop them in without any adapter layer.
 *
 * `action.href` deep-links to `/assistant?prompt=…` so tapping a card
 * opens the chat with a plausible starter prompt already loaded —
 * demo doesn't have to actually DO the work, just navigate correctly.
 */
export const demoNovaSuggestions = [
  {
    id: 'demo-sug-1',
    kind: 'upcoming_shoot',
    title: "Sarah & Ben's wedding is 4 weeks out",
    body: 'Want me to draft their day-of timeline based on the coverage plan?',
    priority: 'high',
    observedAt: new Date(now.getTime() - 2 * 3600_000).toISOString(),
    action: {
      label: 'Draft the timeline',
      href: '/assistant?prompt=' + encodeURIComponent("Draft Sarah & Ben's wedding-day timeline"),
      dismissLabel: 'Not now',
    },
    projectId: 'demo-p-1',
    clientId: 'demo-c-1',
    novaState: 'has-suggestion',
  },
  {
    id: 'demo-sug-2',
    kind: 'fresh_delivery',
    title: "3 photos from Ember's shoot are untagged",
    body: 'Batch-classify with Nova auto-tag so they surface in gallery search.',
    priority: 'medium',
    observedAt: new Date(now.getTime() - 5 * 3600_000).toISOString(),
    action: {
      label: 'Batch classify',
      href: '/assistant?prompt=' + encodeURIComponent('Batch auto-tag the untagged photos from Ember'),
      dismissLabel: 'Skip',
    },
    projectId: 'demo-p-7',
    clientId: 'demo-c-5',
    novaState: 'curious',
  },
  {
    id: 'demo-sug-3',
    kind: 'delivery_followup',
    title: "Kayla + James haven't opened their delivery",
    body: 'Delivered 3 days ago, still unread. Send a warm nudge?',
    priority: 'medium',
    observedAt: new Date(now.getTime() - 8 * 3600_000).toISOString(),
    action: {
      label: 'Send a nudge',
      href: '/assistant?prompt=' + encodeURIComponent('Draft a warm follow-up to Kayla + James'),
      dismissLabel: 'Later',
    },
    projectId: 'demo-p-3',
    clientId: 'demo-c-8',
    novaState: 'has-suggestion',
  },
  {
    id: 'demo-sug-4',
    kind: 'outstanding_invoice',
    title: "You've spent $2,340 on gear this month",
    body: 'Want me to categorise them so tax season is a one-tap export?',
    priority: 'low',
    observedAt: new Date(now.getTime() - 12 * 3600_000).toISOString(),
    action: {
      label: 'Categorise expenses',
      href: '/assistant?prompt=' + encodeURIComponent("Categorise this month's equipment expenses"),
      dismissLabel: 'Not now',
    },
    novaState: 'curious',
  },
  {
    id: 'demo-sug-5',
    kind: 'idle_inquiry',
    title: "Willow Boutique's contract expires Friday",
    body: "Draft a renewal proposal? I'll pull last cycle's numbers as a starting point.",
    priority: 'high',
    observedAt: new Date(now.getTime() - 18 * 3600_000).toISOString(),
    action: {
      label: 'Draft the renewal',
      href: '/assistant?prompt=' + encodeURIComponent("Draft Willow Boutique's contract renewal"),
      dismissLabel: 'Snooze',
    },
    projectId: 'demo-p-9',
    clientId: 'demo-c-9',
    novaState: 'has-suggestion',
  },
  {
    id: 'demo-sug-6',
    kind: 'fresh_delivery',
    title: 'Nova Coffee gallery has 12 unresolved comments',
    body: "Batch-review with me — I'll cluster the notes so you can knock them out fast.",
    priority: 'medium',
    observedAt: new Date(now.getTime() - 22 * 3600_000).toISOString(),
    action: {
      label: 'Batch review',
      href: '/assistant?prompt=' + encodeURIComponent('Cluster the unresolved Nova Coffee gallery comments'),
      dismissLabel: 'Later',
    },
    projectId: 'demo-p-4',
    clientId: 'demo-c-4',
    novaState: 'curious',
  },
];

/**
 * Three seeded Nova conversations so the History drawer isn't empty
 * when the keynote opens the chat. Timestamps spread across the last
 * week so the "3 days ago / yesterday" subtitles land right; bodies
 * read as plausible Nova replies — not too short, not paragraphs of
 * markdown.
 */
export const demoNovaConversations = [
  {
    id: 'demo-nc-1',
    lastActive: new Date(now.getTime() - 1 * dayMs).toISOString(),
    messages: [
      {
        role: 'user',
        content: 'Draft a delivery email for Marcus + Elle — their engagement gallery is ready.',
      },
      {
        role: 'assistant',
        content:
          "Here's a draft — it leans warm and a little playful since their session was that golden-hour rooftop shoot:\n\nHi Marcus & Elle,\n\nYour engagement gallery is live. There are 62 selects in there — including that ridiculous laugh from the last frame on the fire escape, which might already be my favourite from the whole session. Take your time going through it, favourite everything that stops you, and I'll pull those into the print + album picks.\n\nLink: [gallery URL]\nPassword: onthebridge\n\nCan't wait to hear what you think.\n\n—",
      },
      {
        role: 'user',
        content: 'Warmer. Less business, more like a friend sending it.',
      },
      {
        role: 'assistant',
        content:
          "Got it — softer, more casual:\n\nHey you two —\n\nOK, gallery's up. I was smiling the whole way through the edit. The rooftop light did exactly what we hoped it would, and there's a frame of you both mid-laugh on the fire escape that I think is going to be the one.\n\nGo through it whenever you have a quiet hour. Favourite anything that stops you and I'll build the print + album picks around those.\n\nLink: [gallery URL]  ·  Password: onthebridge\n\nSo happy for you both.\n\n—",
      },
      {
        role: 'user',
        content: 'Perfect. Send it.',
      },
    ],
  },
  {
    id: 'demo-nc-2',
    lastActive: new Date(now.getTime() - 3 * dayMs).toISOString(),
    messages: [
      {
        role: 'user',
        content: "What's on my plate this week?",
      },
      {
        role: 'assistant',
        content:
          "Here's the shape of the next five days:\n\n1. **Today** — Sarah & Ben's wedding-day timeline review is waiting on you (ceremony start moved 30 minutes, needs a re-pass on golden-hour block).\n2. **Wednesday** — Chen family consult, 11am. Their brief says editorial submission, so bring the Vogue mood decks.\n3. **Thursday** — Ember Restaurant re-edit; kitchen wants two more hero shots of the tasting menu.\n4. **Friday** — Willow Boutique contract deadline. I've got a renewal draft ready when you want to look.\n5. **Saturday** — weekend off. You blocked it for family, and I've quietly declined the two inquiries that tried to book you into it.\n\nAnything I should shuffle?",
      },
    ],
  },
  {
    id: 'demo-nc-3',
    lastActive: new Date(now.getTime() - 5 * dayMs).toISOString(),
    messages: [
      {
        role: 'user',
        content: 'Help me price the Whitmore Estate shoot.',
      },
      {
        role: 'assistant',
        content:
          'Happy to. Two quick questions so the number lands right:\n\n1. Are we going day-rate or project-rate? Estates tend to want a fixed number so their board can approve it, but day-rate gives you room if the scope creeps.\n2. What editing turnaround are you comfortable committing to? Two weeks vs. four weeks moves the price meaningfully.',
      },
      {
        role: 'user',
        content: 'Project rate. Three-week turnaround.',
      },
      {
        role: 'assistant',
        content:
          "OK — here's a starting-point proposal:\n\n**Whitmore Estate — Portrait & Grounds**\nProject rate: $4,800\nIncludes: full-day coverage (up to 8 hours), scouting call the week before, 60+ edited images delivered in 3 weeks, one round of revisions, private online gallery.\nOptional add-ons: same-day preview reel (+$450), aerial coverage (+$700), family album (+$1,200).\n\nWant me to format this as a sendable estimate?",
      },
    ],
  },
];

/**
 * The single pre-formatted string injected into every Nova chat POST
 * when Preview Studio is on. Same content is used on iOS (mirrored
 * in `DemoSeed.novaChatContext`) so on-stage responses read
 * consistently across platforms.
 *
 * Kept intentionally boring/factual — the LLM does the persona; this
 * is just the facts about the demo studio it needs to sound like an
 * assistant that knows the shop.
 */
export function buildDemoChatContext(): string {
  return [
    'DEMO CONTEXT — this studio\'s current active roster:',
    'Clients: Sarah + Ben Martinez (upcoming wedding, 4 wk), The Hollins Family '
      + '(delivered last mo), Nova Coffee Co (commercial branding, ongoing), '
      + 'Marcus + Elle (wedding delivered last wk), The Chen Family (booked next mo), '
      + 'Willow Boutique (product photog), Dr. Reyes (personal branding), '
      + 'Kayla + James (engagement session), Whitmore Estate (real estate), '
      + 'Ember Restaurant (food + interior), The Andersson Wedding (past), '
      + 'Sunset Kids (school portraits).',
    '',
    'Active projects: Sarah + Ben Wedding (Aug 15), Chen Family Portrait (Sept 10), '
      + 'Willow Boutique product refresh, Nova Coffee Co brand pipeline, '
      + 'Kayla + James engagement, Whitmore Estate scout.',
    '',
    'Upcoming this week: Wed — Chen family consult; Thu — Ember re-edit; '
      + 'Fri — Willow contract deadline; Sat — Sarah + Ben venue walkthrough.',
    '',
    'Recent inquiries: 15 total, 3 pending follow-up (Bloom Bridal, '
      + 'Lakeside Weddings, Nova Design Studio).',
    '',
    'Studio finances (this month): $24,800 received, $9,800 outstanding, '
      + '$2,340 in equipment expenses to categorise. Nova credits: 1,247 remaining '
      + 'on the Studio plan (renews in 18 days).',
    '',
    'Answer as if you know this studio well. Don\'t say "I don\'t see any data" — '
      + 'reference the above coherently when relevant.',
  ].join('\n');
}
